// BookingDashboard.js
import React, { useEffect, useState } from 'react';
import { auth } from "./firebase";
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import MyInformation from './MyInformation';  // Add this line
import PaymentMethods from './PaymentMethods';  // Add this line
import StripeConnectSetup from './StripeConnectSetup';
import { useUser } from './usersContext';  // Add this import
import './Dashboard.css'; // Add this line to import the CSS
import { queueMail } from './SendMail';
import { createGoogleMeetLink } from './googleMeetUtils'; // Import Google Meet utility
import LoadingOverlay from './LoadingOverlay'; // Add loading overlay
import ConfirmationModal from './components/ConfirmationModal';
import { useConfirmationModal } from './hooks/useConfirmationModal';
import PaymentConfirmationModal from './components/PaymentConfirmationModal';
import StatusMessage from './components/StatusMessage';
import { useStatusMessage } from './hooks/useStatusMessage';
import api from './utils/api'; // Import the API utility
import { useDeviceDetection } from './hooks/useDeviceDetection';

const toLocalMidnightISOString = (dateObj) => {
  const d = new Date(dateObj);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

// Helper to format participant display (name and email)
function formatParticipant(name, email, fallbackLabel = 'N/A') {
  if (name && email) return `${name} (${email})`;
  if (name) return `${name}`;
  if (email) return `${email}`;
  return fallbackLabel;
}

const BookingDashboard = ({ defaultPage }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userName, setUserName] = useState("User");
  const [userAvatar, setUserAvatar] = useState(null);
  const [availability, setAvailability] = useState({
    start: '',
    end: ''
  });
  const [user, setUser] = useState(null);  // Add this line
  const [activePage, setActivePage] = useState(defaultPage || 'calendar');
  const [paymentPageMessage, setPaymentPageMessage] = useState(null); // Message to show on payment page
  const { currentUser, userDoc } = useUser();
  const [userType, setUserType] = useState(null);
  
  // Device detection and mobile menu state
  const { deviceType, isMobile } = useDeviceDetection();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  
  const [recurringWeekly, setRecurringWeekly] = useState(false);
  const [recurringWeeks, setRecurringWeeks] = useState(4); // Default to 4 weeks
  const [mySessions, setMySessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);  
    // Loading states for different operations
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
    // Additional loading states for specific operations
  const [isAddingEvent, setIsAddingEvent] = useState(false);
  const [isReserving, setIsReserving] = useState(false);
  const [isDeletingEvent, setIsDeletingEvent] = useState(false);
  const [isUpdatingEvent, setIsUpdatingEvent] = useState(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
    const [tutorSearch, setTutorSearch] = useState('');
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [showTutorDropdown, setShowTutorDropdown] = useState(false);
  const [allTutors, setAllTutors] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showStudentEventPopup, setShowStudentEventPopup] = useState(false);
    // Partial reservation state
  const [showPartialReservationModal, setShowPartialReservationModal] = useState(false);
  const [selectedSlotForPartialReservation, setSelectedSlotForPartialReservation] = useState(null);
  const [partialStartTime, setPartialStartTime] = useState('12:00 PM');
  const [partialEndTime, setPartialEndTime] = useState('1:00 PM');
  const [partialTimeValidationMessage, setPartialTimeValidationMessage] = useState('');
    // Monthly sessions data
  const [monthlyEvents, setMonthlyEvents] = useState([]);
  const [isLoadingMonthlyData, setIsLoadingMonthlyData] = useState(false);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);

  // Calendar date state (must be declared before useEffect hooks that reference them)
  const currentDate = new Date();
  const [currentMonth, setCurrentMonth] = useState(currentDate.getMonth());
  const [currentYear, setCurrentYear] = useState(currentDate.getFullYear());  
  const [selectedDate, setSelectedDate] = useState(currentDate);  // Animation state for events fold-out
  const [isAnimatingEvents, setIsAnimatingEvents] = useState(false);
  const [animationPhase, setAnimationPhase] = useState('none'); // 'none', 'folding-in', 'folding-out'
  const [isClickDisabled, setIsClickDisabled] = useState(false); // Prevent rapid clicking
  // Animation state for calendar navigation
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigationDirection, setNavigationDirection] = useState(''); // 'left', 'right'
  const [showMonthHighlight, setShowMonthHighlight] = useState(false);
  const [isNavigatingMonth, setIsNavigatingMonth] = useState(false); // Track month navigation specifically
  // Confirmation modal
  const confirmationModal = useConfirmationModal();
  
  // Payment confirmation modal state
  const [showPaymentConfirmModal, setShowPaymentConfirmModal] = useState(false);
  const [pendingPaymentAction, setPendingPaymentAction] = useState(null);
  const [paymentSessionDetails, setPaymentSessionDetails] = useState(null);
  const [hidePaymentConfirmation, setHidePaymentConfirmation] = useState(false);
  
  // Status message for professional notifications
  const statusMessage = useStatusMessage();

  const subjectOptions = [
    "math", "physics", "chemistry", "biology", "french", "english", "spanish",
    "history", "philosophy", "coding", "music", "geography"
  ];

  const [subjectFilter, setSubjectFilter] = useState([]);
  const [pendingSubjectFilter, setPendingSubjectFilter] = useState([]);
  const [hasUnappliedSubjectChanges, setHasUnappliedSubjectChanges] = useState(false);

  const handleSubjectFilterChange = (subject) => {
    setPendingSubjectFilter((prev) => {
      const newFilter = prev.includes(subject)
        ? prev.filter((s) => s !== subject)
        : [...prev, subject];
      
      // Check if the new pending filter is different from the currently applied filter
      const isDifferentFromApplied = JSON.stringify([...newFilter].sort()) !== JSON.stringify([...subjectFilter].sort());
      setHasUnappliedSubjectChanges(isDifferentFromApplied);
      
      return newFilter;
    });
  };
  const applySubjectFilters = async () => {
    setSubjectFilter(pendingSubjectFilter);
    setHasUnappliedSubjectChanges(false);
    
    // Note: We don't need to refetch data since we're doing client-side filtering
    // The useEffect that watches subjectFilter will automatically update the displayed events
  };

  const resetSubjectFilters = async () => {
    setSubjectFilter([]);
    setPendingSubjectFilter([]);
    setHasUnappliedSubjectChanges(false);
    
  };

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (user) {
        setUserName(user.displayName || "User");
        setUserAvatar(user.photoURL);
        setUser(user);  // Add this line
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchUserType = async () => {
      if (currentUser) {
        try {
          const response = await api.post('/api/checkUserStatus', { email: currentUser.email });
          const data = await response.json();
          setUserType(data.docData.userType); // Access userType from docData
        } catch (error) {
          console.error('Error fetching user type:', error);
        }
      }
    };

    fetchUserType();
  }, [currentUser]);
  
  // Sync hidePaymentConfirmation with userDoc
  useEffect(() => {
    if (userDoc?.hidePaymentConfirmation) {
      setHidePaymentConfirmation(true);
    }
  }, [userDoc]);
  
  useEffect(() => {
    const handleFocus = () => {
      // Only refresh data on calendar page to prevent interference with payment forms
      if (currentUser && userType && activePage === 'calendar') {
        setIsLoading(true);
        setLoadingMessage('Refreshing calendar data...');
        fetchMonthlyEvents(currentYear, currentMonth).finally(() => {
          setIsLoading(false);
          setLoadingMessage('');
        });
      }
    };

    const handleVisibilityChange = () => {
      // Only refresh data on calendar page to prevent interference with payment forms
      if (!document.hidden && currentUser && userType && activePage === 'calendar') {
        setIsLoading(true);
        setLoadingMessage('Refreshing calendar data...');
        fetchMonthlyEvents(currentYear, currentMonth).finally(() => {
          setIsLoading(false);
          setLoadingMessage('');
        });
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser, userType, currentYear, currentMonth, activePage]);  useEffect(() => {
    // Refresh all dashboard data when user enters the dashboard (component mounts)
    if (currentUser) {
      // Fetch user type, sessions, and events for the current month
      (async () => {
        try {
          setIsLoading(true);
          setLoadingMessage('Loading dashboard...');
          
          // Always refresh user type
          const response = await api.post('/api/checkUserStatus', { email: currentUser.email });
          const data = await response.json();
          setUserType(data.docData.userType);

          // Refresh monthly events
          await fetchMonthlyEvents(currentYear, currentMonth);
        } catch (e) {
          // Optionally handle error
          console.error('Error during initial dashboard load:', e);
        } finally {
          setIsLoading(false);
          setLoadingMessage('');
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);// Refresh monthly events when month/year changes
  useEffect(() => {
    if (currentUser && userType) {
      // Only show global loading if NOT navigating between months
      if (!isNavigatingMonth) {
        setIsLoading(true);
        setLoadingMessage('Loading calendar data...');
      }
      fetchMonthlyEvents(currentYear, currentMonth).finally(() => {
        if (!isNavigatingMonth) {
          setIsLoading(false);
          setLoadingMessage('');
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, currentYear, currentUser, userType]);// Update displayed events when monthly data, selected date, subject filter, or tutor filter changes
  useEffect(() => {
    if (selectedDate && monthlyEvents) {
      if (animationPhase === 'folding-out') {
        // Update events immediately when starting fold-out animation
        updateDisplayedEvents(selectedDate);
      } else if (animationPhase === 'none') {
        // Normal update when no animation is running
        updateDisplayedEvents(selectedDate);
      }
      // Don't update during 'folding-in' phase - let the animation handle it
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyEvents, selectedDate, subjectFilter, selectedTutor, animationPhase]); // Added animationPhase dependency// Note: Tutor filtering is now done client-side only (like subject filtering) to reduce loading times

  // Fetch all tutors for autocomplete and profile photos (on mount)
  useEffect(() => {
    // Fetch tutors regardless of userType to have profile photos available
    api.post('/api/getTutorsBySubjects', { subjects: [] })
      .then(res => res.json())
      .then(data => setAllTutors(data.tutors || []))
      .catch(() => setAllTutors([]));
  }, [userType]);

  // Track if we need to check tutor availability after data loads
  const [pendingTutorCheck, setPendingTutorCheck] = useState(null);

  // Handle incoming navigation requests (tutor selection or payment methods)
  useEffect(() => {
    // Handle payment methods navigation
    if (location.state?.showPaymentMethods && userType !== 'tutor') {
      setActivePage('paymentMethods');
      // Clear the location state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
    
    // Handle tutor selection from Meet the Tutors page
    if (location.state?.selectedTutor && userType !== 'tutor' && allTutors.length > 0) {
      const incomingTutor = location.state.selectedTutor;
      
      // Find the tutor in allTutors array by email
      const tutorFromDb = allTutors.find(t => t.email === incomingTutor.email);
      
      const tutorToSelect = tutorFromDb || incomingTutor;
      
      // Set the selected tutor
      setSelectedTutor(tutorToSelect);
      // Set the search bar text
      setTutorSearch(getFullName(tutorToSelect) || tutorToSelect.email);
      // Mark that we need to check availability once data loads
      setPendingTutorCheck(tutorToSelect);
      
      // Clear the location state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, userType, allTutors]);

  // Check tutor availability after monthly events are loaded
  useEffect(() => {
    // Only proceed if we have loaded initial data and we're not currently loading
    if (pendingTutorCheck && hasLoadedInitialData && !isLoadingMonthlyData) {
      // First check if tutor has availability in current loaded data
      const hasAvailabilityInCurrentMonth = monthlyEvents.some(event => {
        // Only check available slots (not tutorReserved which are pending confirmations)
        if (event.type !== 'available') return false;
        
        const tutorEmailMatch = event.tutorEmail === pendingTutorCheck.email;
        const tutorNameMatch = event.tutorName === (pendingTutorCheck.name || getFullName(pendingTutorCheck));
        
        return tutorEmailMatch || tutorNameMatch;
      });
      
      
      // If tutor has availability in current month, just clear the pending check
      // Don't call handleTutorSelection which might trigger the error message
      if (hasAvailabilityInCurrentMonth) {
        setPendingTutorCheck(null);
        return;
      }
      
      // Only search other months if no availability in current month
      setTimeout(() => {
        handleTutorSelection(pendingTutorCheck);
        setPendingTutorCheck(null);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyEvents, pendingTutorCheck, isLoadingMonthlyData, hasLoadedInitialData]);

  // Fetch all non-tutor users for autocomplete (on mount, for tutors only)
  useEffect(() => {
    if (userType === 'tutor') {
      api.post('/api/getTutorsBySubjects', { subjects: [] })
        .then(res => res.json())
        .then(() => {
          // Now fetch all non-tutors
          api.post('/api/getNonTutors', { email: currentUser.email })
            .then(res => res.json())
            .then(data => setAllStudents(data.students || []))
            .catch(() => setAllStudents([]));
        });
    }
  }, [userType]);  // Add: Refresh data when switching tabs
  useEffect(() => {
    if (!currentUser) return;
    
    // Only refresh when switching TO specific tabs that need data refresh
    // Skip refresh for paymentMethods and information tabs
    if (activePage === 'calendar' && userType) {
      setIsLoading(true);
      setLoadingMessage('Loading calendar data...');
      fetchMonthlyEvents(currentYear, currentMonth).finally(() => {
        setIsLoading(false);
        setLoadingMessage('');
      });
    } else if (activePage === 'mySessions' && userType) {
      // Trigger loading and fetch sessions when switching to mySessions tab
      fetchMySessions();
    }
    // No data fetching for 'paymentMethods' or 'information' tabs
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage]);
  const fetchMySessions = async () => {
    if (!currentUser) return;
    setSessionsLoading(true);
    setIsLoading(true);
    setLoadingMessage('Loading your sessions...');
    
    try {
      let tutorBookings = [];
      if (userType === 'tutor') {
        // Fetch all types for tutors: available, reserved, confirmed
        const res = await api.post('/api/getMySessions', { tutorEmail: currentUser.email, allTypes: true });
        const data = await res.json();
        tutorBookings = data.bookings || [];
      }
      // Fetch as student (only reserved/confirmed)
      const res2 = await api.post('/api/getMySessions', { studentEmail: currentUser.email });
      const data2 = await res2.json();
      const studentBookings = data2.bookings || [];

      // Merge and deduplicate by id
      const all = [...tutorBookings, ...studentBookings].filter(
        (b, idx, arr) => arr.findIndex(x => x.id === b.id) === idx
      );
      // Only show future events
      const now = new Date();
      const future = all.filter(b => {
        let d = b.date;
        if (!d && b.bookingDate) d = b.bookingDate;
        if (d && typeof d === 'object' && d.seconds) {
          d = new Date(d.seconds * 1000);
        } else if (typeof d === 'string' || typeof d === 'number') {
          d = new Date(d);
        }
        return d && !isNaN(d) && d >= now;
      });
      setMySessions(future);
    } catch (e) {
      console.error('Error fetching sessions:', e);
      setMySessions([]);
    } finally {
      setSessionsLoading(false);
      setIsLoading(false);
      setLoadingMessage('');
    }
  };
  
  const handleConfirmSession = async (bookingId, skipPaymentModal = false) => {
    // Find the booking in mySessions or events to get details
    let booking =
      mySessions.find(b => b.id === bookingId) ||
      events.find(b => b.id === bookingId);
    
    // Fallback if not found
    if (!booking) {
      return;
    }

    // If user is a student confirming and hasn't skipped the modal, check if we should show payment confirmation
    if (userType !== 'tutor' && !skipPaymentModal && !hidePaymentConfirmation) {
      // Set up session details for the modal
      setPaymentSessionDetails({
        startTime: booking.startTime,
        endTime: booking.endTime,
        date: booking.date || booking.bookingDate,
        tutorName: booking.tutorName,
        description: booking.text || booking.description
      });
      
      // Store the action to be performed after confirmation
      setPendingPaymentAction({
        type: 'confirm',
        bookingId: bookingId
      });
      
      // Show the payment confirmation modal
      setShowPaymentConfirmModal(true);
      return;
    }
    
    // Proceed with the actual confirmation
    setIsLoading(true);
    setLoadingMessage('Confirming session and processing payment...');
    
    try {
      // If user is a student confirming, check for payment method first
      if (userType !== 'tutor') {
        try {
          // Check if the user has a valid payment method
          const paymentMethodResponse = await api.post('/api/getPaymentMethods', {});
          
          if (!paymentMethodResponse.ok) {
            throw new Error('Failed to verify payment methods');
          }
            const paymentData = await paymentMethodResponse.json();
          
          if (!paymentData.paymentMethods || paymentData.paymentMethods.length === 0) {
            // No payment methods found, show payment methods section in dashboard
            setActivePage('paymentMethods');
            statusMessage.showWarning('Please add a payment method before confirming sessions.');
            return;
          }
          
          // Check if user has a default payment method
          if (!paymentData.paymentMethods.some(pm => pm.isDefault)) {
            setActivePage('paymentMethods');
            statusMessage.showWarning('Please set a default payment method before confirming sessions.');
            return;
          }
        } catch (error) {
          console.error('Error checking payment methods:', error);
          return;
        }
      }

      // Get student name - use saved info or generate from firstName/lastName if available
      let studentName = booking.studentName || '';
      if (!studentName && booking.studentEmail === currentUser.email && userDoc) {
        studentName = userDoc.firstName && userDoc.lastName
          ? `${userDoc.firstName} ${userDoc.lastName}`
          : (userDoc.name || currentUser.displayName || '');
      }      // Call the confirmation endpoint with payment processing
      const confirmResponse = await api.post('/api/confirmAndChargeSession', { 
          bookingId
        });

      if (!confirmResponse.ok) {
        const errorData = await confirmResponse.json();
        
        // Handle specific error codes
        if (errorData.errorCode) {
          switch (errorData.errorCode) {
            case 'CONFIRMATION_IN_PROGRESS':
              statusMessage.showWarning('This booking is already being confirmed. Please wait.');
              await fetchMonthlyEvents(currentYear, currentMonth);
              return;
            case 'TOO_MANY_CONFIRMATION_ATTEMPTS':
              statusMessage.showWarning('Too many confirmation attempts. Please wait a moment and try again.');
              await fetchMonthlyEvents(currentYear, currentMonth);
              return;
            case 'PAYMENT_IN_PROGRESS':
              statusMessage.showWarning('Payment is already being processed for this session. Please wait.');
              await fetchMonthlyEvents(currentYear, currentMonth);
              return;
            case 'PAYMENT_FAILED':
              // Show specific payment failure message
              const paymentError = errorData.message || 'Your payment could not be processed. Please check your payment method.';
              statusMessage.showError(paymentError);
              
              // If it's a card issue, automatically navigate to payment methods after a delay
              if (errorData.stripeCode === 'card_declined' || 
                  errorData.stripeCode === 'expired_card' || 
                  errorData.stripeCode === 'insufficient_funds') {
                setTimeout(() => {
                  setPaymentPageMessage({ type: 'error', text: paymentError });
                  setActivePage('paymentMethods');
                }, 2000);
              } else {
                // Only refresh calendar if we're not navigating away
                await fetchMonthlyEvents(currentYear, currentMonth);
              }
              return;
            case 'INVALID_PAYMENT_REQUEST':
              statusMessage.showError('Invalid payment request. Please contact support if this persists.');
              await fetchMonthlyEvents(currentYear, currentMonth);
              return;
            case 'PAYMENT_SERVICE_UNAVAILABLE':
              statusMessage.showError('Payment service is temporarily unavailable. Please try again in a few moments.');
              await fetchMonthlyEvents(currentYear, currentMonth);
              return;
            case 'PAYMENT_CONFIG_ERROR':
              statusMessage.showError('Payment configuration error. Please contact support.');
              await fetchMonthlyEvents(currentYear, currentMonth);
              return;
            default:
              throw new Error(errorData.message || 'Failed to confirm session');
          }
        } else {
          throw new Error(errorData.message || 'Failed to confirm session');
        }
      }

      const confirmData = await confirmResponse.json();

      // Compose recipients: both tutor and student if available
      const recipients = [];
      if (booking.tutorEmail) recipients.push(booking.tutorEmail);
      if (booking.studentEmail) recipients.push(booking.studentEmail);

      // Generate a Google Meet link for the session
      let meetLink = null;
      try {
        // Format names for the meeting
        const tutorName = booking.tutorName || "Tutor";
        const studentName = booking.studentName || (userDoc?.firstName && userDoc?.lastName )
          ? `${userDoc.firstName} ${userDoc.lastName}` 
          : (userDoc?.name || currentUser.displayName || "Student");
        
        
        // Create a Google Meet link via our utility
        meetLink = await createGoogleMeetLink({
          bookingId,
          date: booking.date || booking.bookingDate,
          startTime: booking.startTime,
          endTime: booking.endTime,
          tutorName,
          studentName,
          tutorEmail: booking.tutorEmail,
          studentEmail: booking.studentEmail,
          text: booking.description || booking.text || `Tutoring session (${tutorName} and ${studentName})`
        });
      } catch (error) {
        console.error('Error creating Google Meet link:', error);
      }

      // Create a session date string in readable format
      let sessionDateString = 'scheduled date';
      try {
        let sessionDate = booking.date;
        if (!sessionDate && booking.bookingDate) sessionDate = booking.bookingDate;
        
        if (sessionDate && typeof sessionDate === 'object' && sessionDate.seconds) {
          sessionDate = new Date(sessionDate.seconds * 1000);
        } else if (typeof sessionDate === 'string' || typeof sessionDate === 'number') {
          sessionDate = new Date(sessionDate);
        }
        
        if (sessionDate && !isNaN(sessionDate)) {
          sessionDateString = `${monthsOfYear[sessionDate.getMonth()]} ${sessionDate.getDate()}, ${sessionDate.getFullYear()}`;
        }
      } catch (formatError) {
        console.error('Error formatting date:', formatError);
      }      // Generate cancellation token
      let cancellationLink = null;
      try {
        const tokenResponse = await api.post('/api/generateCancellationToken', { 
          bookingId 
        });
        
        if (tokenResponse.ok) {
          const tokenData = await tokenResponse.json();
          cancellationLink = `${window.location.origin}/cancel-session/${tokenData.token}`;
        }
      } catch (tokenError) {
        console.error('Error generating cancellation token:', tokenError);
        // Continue without cancellation link if this fails
      }

      // Format payment information
       // Send confirmation email with Google Meet link if available
      await queueMail({
        to: recipients,
        subject: 'Session Confirmed with Google Meet Link',
        text: `Your tutoring session (booking ID: ${bookingId}) has been confirmed for ${sessionDateString} at ${booking.startTime}. ${meetLink ? `Join with Google Meet: ${meetLink}` : 'A Google Meet link could not be generated at this time.'}${cancellationLink ? `\n\nTo cancel this session: ${cancellationLink}` : ''}`,
        html: `
          <h2>Your Bright-Bridge Tutoring Session is Confirmed!</h2>
          <p><strong>Date:</strong> ${sessionDateString}</p>
          <p><strong>Time:</strong> ${booking.startTime} - ${booking.endTime}</p>
          <p><strong>Tutor:</strong> ${booking.tutorName || 'N/A'}, ${booking.tutorEmail} </p>
          <p><strong>Student:</strong> ${studentName || booking.studentName || 'N/A'}, ${booking.studentEmail} </p>
          ${booking.text || booking.description ? `<p><strong>Details:</strong> ${booking.text || booking.description}</p>` : ''}

          ${meetLink ? `
            <div style="margin: 20px 0;">
              <a href="${meetLink}" style="background-color: #1a73e8; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; font-weight: bold; display: inline-block;">
                Join Google Meet Now
              </a>
            </div>
          ` : '<p>A Google Meet link could not be generated. Your tutor will contact you with meeting details.</p>'}
          
          ${cancellationLink ? `
            <div style="background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 4px; padding: 12px; margin: 20px 0;">
              <p style="margin: 0; color: #856404; font-size: 14px;">
                <strong>Need to cancel?</strong> You can cancel this session via the Bright Bridge Dashboard: https://brightbridgetutors.ca/Dashboard</a> until the start time.
              </p>
              <p style="margin: 10px 0 0 0; color: #856404; font-size: 12px;">
                <strong>Refund Policy:</strong> Full refunds are available when cancelled 24+ hours in advance. No refunds for cancellations within 24 hours of session start.
              </p>
            </div>
          ` : ''}
        
          
          <p style="color: #666; font-size: 12px;">Booking ID: ${bookingId}</p>
        `
      });

      // If we successfully created a Meet link, save it to the booking in Firestore
      if (meetLink) {
        await api.put(`/api/updateBooking/${bookingId}`, { 
            email: currentUser.email, // Add user email for verification
            meetLink 
          });
      }      // Refresh the sessions data
      if (activePage === 'mySessions') {
        await fetchMySessions();
      }
      
      // Also refresh monthly events if we're in calendar view
      if (activePage === 'calendar') {
        await fetchMonthlyEvents(currentYear, currentMonth);
      }
      // Refresh the calendar and sessions
      await fetchMonthlyEvents(currentYear, currentMonth);
      await fetchMySessions();
      
    } catch (e) {
      console.error('Session confirmation error:', e);
      
      // Handle specific error cases with user-friendly messages
      if (e.message === 'BOOKING_NOT_FOUND') {
        statusMessage.showError('This session no longer exists. The page will refresh.');
        await fetchMonthlyEvents(currentYear, currentMonth);
      } else if (e.message === 'ALREADY_CONFIRMED') {
        statusMessage.showWarning('This session has already been confirmed.');
        await fetchMonthlyEvents(currentYear, currentMonth);
      } else if (e.message === 'INVALID_BOOKING_STATE') {
        statusMessage.showError('This session cannot be confirmed in its current state.');
        await fetchMonthlyEvents(currentYear, currentMonth);
      } else if (e.message === 'NO_PAYMENT_METHOD' || e.message === 'Payment method required') {
        const noPaymentMsg = 'Please add a payment method before confirming sessions. Redirecting to payment methods...';
        statusMessage.showError(noPaymentMsg);
        setTimeout(() => {
          setPaymentPageMessage({ type: 'warning', text: 'Please add a payment method to confirm sessions.' });
          setActivePage('paymentMethods');
        }, 2000);
        // Don't refresh calendar since we're navigating away
        return;
      } else if (e.message === 'BOOKING_IN_PROGRESS') {
        statusMessage.showWarning('This session is currently being processed. Please wait a moment and try again.');
      } else if (e.message.includes('declined') || e.message.includes('insufficient funds') || e.message.includes('expired')) {
        const paymentErrorMsg = e.message;
        statusMessage.showError(paymentErrorMsg);
        setTimeout(() => {
          setPaymentPageMessage({ type: 'error', text: paymentErrorMsg });
          setActivePage('paymentMethods');
        }, 2000);
        // Don't refresh calendar since we're navigating away
        return;
      } else if (e.message.includes('payment') || e.message.includes('card')) {
        statusMessage.showError(e.message || 'Payment processing failed. Please check your payment method and try again.');
      } else {
        statusMessage.showError(e.message || 'Failed to confirm session. Please try again.');
      }
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const daysOfWeek = ['Sun', 'Mon', 'Tues', 'Weds', 'Thurs', 'Fri', 'Sat'];
  const monthsOfYear = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'  ];

  const [showEventPopup, setShowEventPopup] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventText, setEventText] = useState('');
  const [editingEvent, setEditingEvent] = useState(null);

  const filteredEvents = React.useMemo(() => {
    if (userType === 'tutor' || !selectedTutor) return events;
    return events.filter(ev =>
      (ev.tutorEmail && ev.tutorEmail === selectedTutor.email) ||
      (ev.tutorName && ev.tutorName === selectedTutor.name)
    );
  }, [events, userType, selectedTutor]);
  // 1) Define time increments for dropdowns (12-hour format for existing functionality)
  const timeIncrements = [
    '12:00','12:15','12:30','12:45',
    '1:00','1:15','1:30','1:45',
    '2:00','2:15','2:30','2:45',
    '3:00','3:15','3:30','3:45',
    '4:00','4:15','4:30','4:45',
    '5:00','5:15','5:30','5:45',
    '6:00','6:15','6:30','6:45',
    '7:00','7:15','7:30','7:45',
    '8:00','8:15','8:30','8:45',
    '9:00','9:15','9:30','9:45',
    '10:00','10:15','10:30','10:45',
    '11:00','11:15','11:30','11:45'
  ];

  // Full time increments for partial reservation (24-hour display with AM/PM)
  const fullTimeIncrements = [
    '12:00 AM','12:15 AM','12:30 AM','12:45 AM',
    '1:00 AM','1:15 AM','1:30 AM','1:45 AM',
    '2:00 AM','2:15 AM','2:30 AM','2:45 AM',
    '3:00 AM','3:15 AM','3:30 AM','3:45 AM',
    '4:00 AM','4:15 AM','4:30 AM','4:45 AM',
    '5:00 AM','5:15 AM','5:30 AM','5:45 AM',
    '6:00 AM','6:15 AM','6:30 AM','6:45 AM',
    '7:00 AM','7:15 AM','7:30 AM','7:45 AM',
    '8:00 AM','8:15 AM','8:30 AM','8:45 AM',
    '9:00 AM','9:15 AM','9:30 AM','9:45 AM',
    '10:00 AM','10:15 AM','10:30 AM','10:45 AM',
    '11:00 AM','11:15 AM','11:30 AM','11:45 AM',
    '12:00 PM','12:15 PM','12:30 PM','12:45 PM',
    '1:00 PM','1:15 PM','1:30 PM','1:45 PM',
    '2:00 PM','2:15 PM','2:30 PM','2:45 PM',
    '3:00 PM','3:15 PM','3:30 PM','3:45 PM',
    '4:00 PM','4:15 PM','4:30 PM','4:45 PM',
    '5:00 PM','5:15 PM','5:30 PM','5:45 PM',
    '6:00 PM','6:15 PM','6:30 PM','6:45 PM',
    '7:00 PM','7:15 PM','7:30 PM','7:45 PM',
    '8:00 PM','8:15 PM','8:30 PM','8:45 PM',
    '9:00 PM','9:15 PM','9:30 PM','9:45 PM',
    '10:00 PM','10:15 PM','10:30 PM','10:45 PM',
    '11:00 PM','11:15 PM','11:30 PM','11:45 PM'
  ];

  // 2) Use separate states for start/end times and AM/PM
  const [startTime, setStartTime] = useState('12:00');
  const [startAmPm, setStartAmPm] = useState('AM');
  const [endTime, setEndTime] = useState('12:15');
  const [endAmPm, setEndAmPm] = useState('AM');
  const convertTimeToMinutes = (time, ampm) => {
    const [hours, minutes] = time.split(':').map(Number);
    let totalMinutes = hours * 60 + minutes;
    
    if (ampm === 'PM' && hours !== 12) {
      totalMinutes += 12 * 60;
    } else if (ampm === 'AM' && hours === 12) {
      totalMinutes = minutes;
    }
    
    return totalMinutes;
  };

  // Helper function to convert full time format (e.g., "1:30 PM") to minutes
  const convertFullTimeToMinutes = (fullTime) => {
    if (!fullTime || typeof fullTime !== 'string') return 0;
    
    const parts = fullTime.split(' ');
    if (parts.length !== 2) return 0;
    
    const [timePart, ampm] = parts;
    const [hours, minutes] = timePart.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) return 0;
    
    let totalMinutes = hours * 60 + (minutes || 0);
    
    if (ampm === 'PM' && hours !== 12) {
      totalMinutes += 12 * 60;
    } else if (ampm === 'AM' && hours === 12) {
      totalMinutes = minutes || 0;
    }
    
    return totalMinutes;
  };
  // Helper function to validate time range for full time format
  const isValidFullTimeRange = (startFullTime, endFullTime) => {
    const startMinutes = convertFullTimeToMinutes(startFullTime);
    const endMinutes = convertFullTimeToMinutes(endFullTime);
    // Enforce minimum 30-minute session duration
    return endMinutes >= startMinutes + 15;
  };
    const isValidTimeRange = (startTime, startAmPm, endTime, endAmPm) => {
    const startMinutes = convertTimeToMinutes(startTime, startAmPm);
    const endMinutes = convertTimeToMinutes(endTime, endAmPm);
    // Enforce minimum 30-minute session duration
    return endMinutes >= startMinutes + 15;
  };

  const roundToNearestFifteen = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const totalMinutes = (hours * 60) + minutes;
    const roundedMinutes = Math.round(totalMinutes / 15) * 15;
    
    const newHours = Math.floor(roundedMinutes / 60);
    const newMinutes = roundedMinutes % 60;
    
    // Format hours to 12-hour format
    const formattedHours = newHours === 0 ? 12 : 
                          newHours > 12 ? newHours - 12 : 
                          newHours;
    
    return `${formattedHours}:${newMinutes.toString().padStart(2, '0')}`;
  };

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  const prevMonth = () => {
    if (isNavigating) return; // Prevent navigation during animation
    
    setIsNavigating(true);
    setIsNavigatingMonth(true); // Set month navigation flag
    setNavigationDirection('left');
    
    // Small delay to let animation start
    setTimeout(() => {
      setCurrentMonth((prevMonth) => (prevMonth === 0 ? 11 : prevMonth - 1));
      setCurrentYear((prevYear) => (currentMonth === 0 ? prevYear - 1 : prevYear));
      
      // Show blue highlight briefly after transition
      setShowMonthHighlight(true);
      setTimeout(() => {
        setShowMonthHighlight(false);
      }, 800); // Blue highlight duration
      
      // Reset animation state after transition
      setTimeout(() => {
        setIsNavigating(false);
        setIsNavigatingMonth(false); // Clear month navigation flag
        setNavigationDirection('');
      }, 300); // Duration matches CSS animation
    }, 150);
  }
  
  const nextMonth = () => {
    if (isNavigating) return; // Prevent navigation during animation
    
    setIsNavigating(true);
    setIsNavigatingMonth(true); // Set month navigation flag
    setNavigationDirection('right');
    
    // Small delay to let animation start
    setTimeout(() => {
      setCurrentMonth((prevMonth) => (prevMonth === 11 ? 0 : prevMonth + 1));
      setCurrentYear((prevYear) => (currentMonth === 11 ? prevYear + 1 : prevYear));
      
      // Show blue highlight briefly after transition
      setShowMonthHighlight(true);
      setTimeout(() => {
        setShowMonthHighlight(false);
      }, 800); // Blue highlight duration
      
      // Reset animation state after transition
      setTimeout(() => {
        setIsNavigating(false);
        setIsNavigatingMonth(false); // Clear month navigation flag
        setNavigationDirection('');
      }, 300); // Duration matches CSS animation
    }, 150);
  }

  // Utility function to refresh all dashboard data
  const refreshDashboardData = async () => {
    if (!currentUser || !userType) return;
    
    try {
      // Refresh monthly events for calendar
      await fetchMonthlyEvents(currentYear, currentMonth);
    } catch (error) {
      console.error('Error refreshing dashboard data:', error);
    }
  };  // New function to fetch all sessions for the current month
  const fetchMonthlyEvents = async (year, month) => {
    if (!currentUser || !userType) return;
    
    setIsLoadingMonthlyData(true);
    try {
      if (userType === 'tutor') {        // Fetch only this tutor's bookings for the month
        const response = await api.post('/api/getTutorBookingsForMonth', {
            email: currentUser.email,
            year: year,
            month: month
            // Remove subjectFilter - we'll do client-side filtering
          });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setMonthlyEvents(data.bookings || []);
        setHasLoadedInitialData(true);
        return;
      }      // For non-tutors, fetch all relevant bookings for the month
      if (userType !== 'tutor') {
        const response = await api.post('/api/getStudentBookingsForMonth', {
            year: year,
            month: month
            // Remove subjectFilter - we'll do client-side filtering only
          });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setMonthlyEvents(data.bookings || []);
        setHasLoadedInitialData(true);
        return;
      }
    } catch (error) {
      console.error('Error fetching monthly events:', error);
      setMonthlyEvents([]);
      setHasLoadedInitialData(true);    } finally {
      setIsLoadingMonthlyData(false);
    }
  };

  const updateDisplayedEvents = (date) => {
    // Filter monthly events for the selected date from pre-loaded data
    if (!monthlyEvents || monthlyEvents.length === 0) {
      setEvents([]);
      return;
    }

    const dateKey = toLocalMidnightISOString(date);
    let filteredEvents = monthlyEvents.filter(event => {
      let eventDate = event.date || event.bookingDate;
      if (eventDate && typeof eventDate === 'object' && eventDate.seconds) {
        eventDate = new Date(eventDate.seconds * 1000);
      } else if (typeof eventDate === 'string' || typeof eventDate === 'number') {
        eventDate = new Date(eventDate);
      }
      
      if (!eventDate || isNaN(eventDate)) return false;
      
      return toLocalMidnightISOString(eventDate) === dateKey;
    });    // Apply client-side tutor filtering for non-tutors  
    if (userType !== 'tutor' && selectedTutor) {
      filteredEvents = filteredEvents.filter(event => {
        const tutorEmailMatch = event.tutorEmail && event.tutorEmail === selectedTutor.email;
        const tutorNameMatch = event.tutorName && event.tutorName === (selectedTutor.name || getFullName(selectedTutor));
        return tutorEmailMatch || tutorNameMatch;
      });
    }    // Apply client-side subject filtering for non-tutors
    if (userType !== 'tutor' && subjectFilter && subjectFilter.length > 0) {
      filteredEvents = filteredEvents.filter(event => {
        // Find the tutor in allTutors array to get their subjects
        const tutor = allTutors.find(t => t.email === event.tutorEmail);
        if (!tutor || !tutor.subjects || tutor.subjects.length === 0) {
          return false; // No tutor data or no subjects, filter out
        }
        
        // Check if tutor teaches at least one of the filtered subjects
        const tutorSubjects = tutor.subjects.map(s => s.toLowerCase());
        const hasMatchingSubject = subjectFilter.some(filterSubject => 
          tutorSubjects.includes(filterSubject.toLowerCase())
        );
        
        return hasMatchingSubject;
      });
    }

    // Apply date/time filtering for available sessions when filters are active
    // Only show sessions on or after current date and time
    const hasActiveFilters = (userType !== 'tutor' && selectedTutor) || 
                            (userType !== 'tutor' && subjectFilter && subjectFilter.length > 0);
    
    if (hasActiveFilters) {
      const now = new Date();
      filteredEvents = filteredEvents.filter(event => {
        // Only apply date/time filter to available sessions
        if (event.type !== 'available') return true;
        
        // Get the session date
        let sessionDate = event.date || event.bookingDate;
        if (sessionDate && typeof sessionDate === 'object' && sessionDate.seconds) {
          sessionDate = new Date(sessionDate.seconds * 1000);
        } else if (typeof sessionDate === 'string' || typeof sessionDate === 'number') {
          sessionDate = new Date(sessionDate);
        }
        
        if (!sessionDate || isNaN(sessionDate)) return false;
        
        // If session is on a future date, show it
        if (sessionDate.toDateString() !== now.toDateString()) {
          return sessionDate >= now;
        }
        
        // If session is today, check the time
        if (event.startTime) {
          try {
            // Parse start time (e.g., "1:00 PM")
            const [timePart, ampm] = event.startTime.split(' ');
            const [hours, minutes] = timePart.split(':').map(Number);
            
            let hour24 = hours;
            if (ampm && ampm.toUpperCase() === 'PM' && hours < 12) {
              hour24 += 12;
            } else if (ampm && ampm.toUpperCase() === 'AM' && hours === 12) {
              hour24 = 0;
            }
            
            // Create a date object for the session start time
            const sessionDateTime = new Date(sessionDate);
            sessionDateTime.setHours(hour24, minutes, 0, 0);
            
            // Only show if session starts at or after current time
            return sessionDateTime >= now;
          } catch (error) {
            console.error('Error parsing session time:', error);
            return true; // Show session if we can't parse the time
          }
        }
        
        return true; // Show session if no start time is available
      });
    }

    setEvents(filteredEvents);
  };

  const handleDayClick = (day) => {
    // Prevent rapid clicking during animations or navigation
    if (isClickDisabled || isAnimatingEvents || isNavigating) return;
    
    const clickedDate = new Date(currentYear, currentMonth, day);
    const today = new Date();
  
    if (clickedDate >= today || isSameDay(clickedDate, today)) {
      // Check if we're selecting a different day
      const isDifferentDay = !isSameDay(clickedDate, selectedDate);
      
      if (isDifferentDay) {
        // Disable clicking during animation
        setIsClickDisabled(true);
        
        // Always animate when switching days
        setIsAnimatingEvents(true);
        setAnimationPhase('folding-in');
          // After fold-in completes, update date and start fold-out
        setTimeout(() => {
          setSelectedDate(clickedDate);
          setAnimationPhase('folding-out');
          
          // After fold-out completes, reset animation state
          setTimeout(() => {
            setIsAnimatingEvents(false);
            setAnimationPhase('none');
            setIsClickDisabled(false); // Re-enable clicking
          }, 350); // Match CSS fold-out duration (0.35s)
        }, 180); // Match CSS fold-in duration (0.18s)
      } else {
        // Same day clicked - just refresh events without animation
        updateDisplayedEvents(clickedDate);
      }
    }
  }

  const handleAddAvailability = () => {
    setShowEventPopup(true);
    setEventText('');
    setStartTime('12:00');
    setStartAmPm('AM');
    setEndTime('12:15');
    setEndAmPm('AM');
    setEditingEvent(null);
    setRecurringWeekly(false); // reset checkbox
    setRecurringWeeks(4); // reset to default 4 weeks
  }

    const isSameDay = (date1, date2) => {
      return (date1.getFullYear() === date2.getFullYear() 
      && date1.getMonth() === date2.getMonth()
      && date1.getDate() === date2.getDate()
    );
  }
  
  // Helper function to check if a day has confirmed sessions
  const dayHasConfirmedSessions = (dayDate) => {
    if (!monthlyEvents || monthlyEvents.length === 0) return false;
    
    return monthlyEvents.some(session => {
      if (session.type !== 'confirmed') return false;
      
      // For tutors, only show highlighting for their own sessions
      if (userType === 'tutor' && session.tutorEmail !== currentUser?.email) {
        return false;
      }
      
      let sessionDate = session.date || session.bookingDate;
      if (sessionDate && typeof sessionDate === 'object' && sessionDate.seconds) {
        sessionDate = new Date(sessionDate.seconds * 1000);
      } else if (typeof sessionDate === 'string' || typeof sessionDate === 'number') {
        sessionDate = new Date(sessionDate);
      }
      
      return sessionDate && !isNaN(sessionDate) && isSameDay(sessionDate, dayDate);
    });
  };
  // Helper function to check if a day has reserved/tutor-reserved sessions
  const dayHasReservedSessions = (dayDate) => {
    if (!monthlyEvents || monthlyEvents.length === 0) return false;
    
    return monthlyEvents.some(session => {
      if (session.type !== 'reserved' && session.type !== 'tutorReserved') return false;
      
      // For tutors, only show highlighting for their own sessions
      if (userType === 'tutor' && session.tutorEmail !== currentUser?.email) {
        return false;
      }
      
      let sessionDate = session.date || session.bookingDate;
      if (sessionDate && typeof sessionDate === 'object' && sessionDate.seconds) {
        sessionDate = new Date(sessionDate.seconds * 1000);
      } else if (typeof sessionDate === 'string' || typeof sessionDate === 'number') {
        sessionDate = new Date(sessionDate);
      }
      
      return sessionDate && !isNaN(sessionDate) && isSameDay(sessionDate, dayDate);
    });
  };// Helper function to check if a day has available sessions matching current filters
  const dayHasFilteredAvailableSessions = (dayDate) => {
    // For tutors, show green highlighting for their own available sessions
    // For non-tutors, only show green feedback when filters are active AND there are actual matching sessions
    const hasActiveFilters = (selectedTutor !== null) || (subjectFilter && subjectFilter.length > 0);
    if (userType !== 'tutor' && !hasActiveFilters) return false;
    
    // If we're still loading or have no data yet, don't show any feedback
    if (!monthlyEvents || monthlyEvents.length === 0) {
      return false; // Don't show green feedback when no data is available
    }
      return monthlyEvents.some(session => {
      // Only check available sessions
      if (session.type !== 'available') return false;
      
      // For tutors, only show highlighting for their own sessions
      if (userType === 'tutor' && session.tutorEmail !== currentUser?.email) {
        return false;
      }
      
      // Check if the session is on the specified day
      let sessionDate = session.date || session.bookingDate;
      if (sessionDate && typeof sessionDate === 'object' && sessionDate.seconds) {
        sessionDate = new Date(sessionDate.seconds * 1000);
      } else if (typeof sessionDate === 'string' || typeof sessionDate === 'number') {
        sessionDate = new Date(sessionDate);
      }
      
      if (!sessionDate || isNaN(sessionDate) || !isSameDay(sessionDate, dayDate)) {
        return false;      }
      
      // For non-tutors, apply filter checks
      if (userType !== 'tutor') {
        // Check tutor filter match
        if (selectedTutor) {
          const tutorEmailMatch = session.tutorEmail && session.tutorEmail === selectedTutor.email;
          const tutorNameMatch = session.tutorName && session.tutorName === (selectedTutor.name || getFullName(selectedTutor));
          if (!tutorEmailMatch && !tutorNameMatch) {
            return false;
          }
        }
        
        // Check subject filter match - implement client-side filtering
        if (subjectFilter && subjectFilter.length > 0) {
          // We need to check if the session's tutor teaches any of the filtered subjects
          // Find the tutor in allTutors array to get their subjects
          const tutor = allTutors.find(t => t.email === session.tutorEmail);
          if (!tutor || !tutor.subjects || tutor.subjects.length === 0) {
          return false; // No tutor data or no subjects, filter out
        }
        
        // Check if tutor teaches at least one of the filtered subjects
        const tutorSubjects = tutor.subjects.map(s => s.toLowerCase());
        const hasMatchingSubject = subjectFilter.some(filterSubject => 
          tutorSubjects.includes(filterSubject.toLowerCase())
        );
          if (!hasMatchingSubject) {
          return false;
        }
        }
      }
      
      // Apply date/time filtering - only show sessions on or after current date and time
      const now = new Date();
      
      // If session is on a future date, show it
      if (sessionDate.toDateString() !== now.toDateString()) {
        return sessionDate >= now;
      }
      
      // If session is today, check the time
      if (session.startTime) {
        try {
          // Parse start time (e.g., "1:00 PM")
          const [timePart, ampm] = session.startTime.split(' ');
          const [hours, minutes] = timePart.split(':').map(Number);
          
          let hour24 = hours;
          if (ampm && ampm.toUpperCase() === 'PM' && hours < 12) {
            hour24 += 12;
          } else if (ampm && ampm.toUpperCase() === 'AM' && hours === 12) {
            hour24 = 0;
          }
          
          // Create a date object for the session start time
          const sessionDateTime = new Date(sessionDate);
          sessionDateTime.setHours(hour24, minutes, 0, 0);
          
          // Only show if session starts at or after current time
          return sessionDateTime >= now;
        } catch (error) {
          console.error('Error parsing session time:', error);
          return true; // Show session if we can't parse the time
        }
      }
      
      return true;
    });
  };
  const addAvailabilityToServer = async (eventData) => {
    try {
      const response = await api.post('/api/addAvailability', {
          email: currentUser.email,
          date: toLocalMidnightISOString(eventData.date),
          startTime: eventData.startTime,
          endTime: eventData.endTime,
          text: eventData.text  
        });
  
      if (!response.ok) {
        const data = await response.json();
        
        // Handle time slot overlap error specifically
        if (response.status === 409) {
          throw new Error('TIME_SLOT_OVERLAP');
        }
        
        throw new Error(data.message || 'Failed to add availability to server');
      }
  
      return await response.json();
    } catch (error) {
      console.error('Error adding availability:', error);
      throw error;
    }
  };

  // Add this utility function near your other utility functions
  const dayRollOverCheck = (time1, ampm1, time2, ampm2) => {
    const [hours1] = time1.split(':').map(Number);
    const [hours2] = time2.split(':').map(Number);
    
    let time1Hours = hours1;
    let time2Hours = hours2;
    
    if (ampm1 === 'PM' && hours1 !== 12) time1Hours += 12;
    if (ampm2 === 'PM' && hours2 !== 12) time2Hours += 12;
    if (ampm1 === 'AM' && hours1 === 12) time1Hours = 0;
    if (ampm2 === 'AM' && hours2 === 12) time2Hours = 0;
    
    return time1Hours <= time2Hours || (time1Hours === 23 && time2Hours === 0);
  };

  const handleDeleteEvent = async (eventId) => {
    setIsDeletingEvent(true);
    setIsLoading(true);
    setLoadingMessage('Deleting event...');
    
    try {
      const response = await api.delete(`/api/deleteBooking/${eventId}`);
      
      if (!response.ok) {
        const data = await response.json();
        
        // Handle specific error cases
        if (response.status === 400 && data.message.includes('Cannot delete reserved')) {
          statusMessage.showError('Cannot delete reserved or confirmed sessions. Please use cancel instead.');
        } else if (response.status === 409) {
          // Handle specific race condition errors for deletion
          if (data.errorCode === 'BOOKING_BEING_RESERVED') {
            statusMessage.showWarning('Someone is currently reserving this slot. Please wait and try again.');
          } else {
            statusMessage.showWarning('This session is currently being processed. Please try again in a moment.');
          }
        } else if (response.status === 404) {
          statusMessage.showError('This session no longer exists.');
        } else {
          statusMessage.showError(data.message || 'Failed to delete session');
        }
        
        // Refresh to show current state
        await fetchMonthlyEvents(currentYear, currentMonth);
        return;
      }
      
      statusMessage.showSuccess('Session deleted successfully');
      // Refresh events after deletion
      await fetchMonthlyEvents(currentYear, currentMonth);
      
    } catch (error) {
      console.error('Error deleting booking:', error);
      statusMessage.showError('Failed to delete session. Please try again.');
    } finally {
      setIsDeletingEvent(false);
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const handleEventEdit = (event) => {
    setSelectedDate(new Date(event.date));
    setEventText(event.text);
    setStartTime(event.startTime.split(' ')[0]);
    setStartAmPm(event.startTime.split(' ')[1]);
    setEndTime(event.endTime.split(' ')[0]);
    setEndAmPm(event.endTime.split(' ')[1]);
    setEditingEvent(event);
    setShowEventPopup(true);
  }

  const addRecurringSlots = async (eventData, isEdit = false) => {
    // eventData.date is a Date object
    const startDate = new Date(eventData.date);
    let current = new Date(startDate);
    const slots = [];
    
    // Generate slots for the selected number of weeks
    for (let i = 0; i < recurringWeeks; i++) {
      slots.push(new Date(current));
      current.setDate(current.getDate() + 7);
    }
    
    // For edit: delete the original, then add all new slots
    if (isEdit && editingEvent) {
      try {
        await api.delete(`/api/deleteBooking/${editingEvent.id}`);
      } catch (error) {
        console.error('Error deleting original event:', error);
        statusMessage.showError('Failed to delete original event');
      }
    }
    
    // Track successes and failures
    let successCount = 0;
    let failureCount = 0;
    const failedDates = [];
    
    // Add all slots, continuing even if some fail
    for (const slotDate of slots) {
      try {
        await addAvailabilityToServer({
          ...eventData,
          date: toLocalMidnightISOString(slotDate),
        });
        successCount++;
      } catch (error) {
        console.error(`Error adding availability for ${slotDate.toLocaleDateString()}:`, error);
        failureCount++;
        failedDates.push(slotDate.toLocaleDateString());
        
        // Don't break the loop - continue with other dates
      }
    }
    
    // Show status message based on results
    if (successCount > 0 && failureCount === 0) {
      statusMessage.showSuccess(`Successfully added ${successCount} recurring availability slots`);
    } else if (successCount > 0 && failureCount > 0) {
      statusMessage.showWarning(`Added ${successCount} slots successfully. ${failureCount} slots failed (possibly due to conflicts): ${failedDates.join(', ')}`);
    } else if (failureCount > 0 && successCount === 0) {
      statusMessage.showError(`Failed to add all ${failureCount} slots. Dates: ${failedDates.join(', ')}`);
    }
  };
  // Update handleEventSubmit to support editing
  const handleEventSubmit = async () => {
    // Convert times to minutes for comparison
    const startMinutes = convertTimeToMinutes(startTime, startAmPm);
    const endMinutes = convertTimeToMinutes(endTime, endAmPm);
    
    // Check if end time is before start time
    if (endMinutes <= startMinutes) {
      statusMessage.showError('End time must be after start time');
      return;
    }
    
    // Check if session is at least 30 minutes long
    if (endMinutes - startMinutes < 30) {
      statusMessage.showError('Sessions must be at least 30 minutes long');
      return;
    }
    
    if (!dayRollOverCheck(startTime, startAmPm, endTime, endAmPm)) {
      statusMessage.showError('Sessions cannot span across midnight');
      return;
    }
    
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("No user found in auth.currentUser");
      return;
    }

    // Set loading state
    setIsAddingEvent(true);
    setIsLoading(true);
    setLoadingMessage(editingEvent ? 'Updating availability...' : 'Adding availability...');

    try {
      const newEvent = {
        id: editingEvent ? editingEvent.id : currentUser.uid + Date.now(),
        date: toLocalMidnightISOString(selectedDate),
        startTime: `${startTime} ${startAmPm}`,
        endTime: `${endTime} ${endAmPm}`,
        text: eventText
      };
      if (userType === 'tutor') {
        if (recurringWeekly) {
          setLoadingMessage('Adding recurring availability...');
          await addRecurringSlots(newEvent, !!editingEvent);
        } else if (editingEvent) {
          setLoadingMessage('Updating availability...');          // Update booking in DB
          await api.put(`/api/updateBooking/${editingEvent.id}`, {
              email: currentUser.email, // Add email for protected route verification
              date: toLocalMidnightISOString(selectedDate),
              startTime: newEvent.startTime,
              endTime: newEvent.endTime,
              text: newEvent.text,
              // Add optimistic locking fields
              expectedType: editingEvent.type || 'available',
              lastKnownUpdate: editingEvent.updatedAt 
                ? (editingEvent.updatedAt._seconds 
                  ? new Date(editingEvent.updatedAt._seconds * 1000).toISOString() 
                  : editingEvent.updatedAt)
                : new Date().toISOString()
            });
        } else {
          setLoadingMessage('Adding availability...');
          // Add new booking
          await addAvailabilityToServer(newEvent);
        }
      }
      setLoadingMessage('Refreshing calendar...');
      await fetchMonthlyEvents(currentYear, currentMonth);
      setEventText('');
      setStartTime('12:00');
      setStartAmPm('AM');
      setEndTime('12:15');
      setEndAmPm('AM');
      setEditingEvent(null);
      setShowEventPopup(false);
      setRecurringWeekly(false);
      setRecurringWeeks(4); // reset to default
    } catch (error) {
      console.error('Error submitting event:', error);
      
      // Handle time slot overlap error
      if (error.message === 'TIME_SLOT_OVERLAP') {
        statusMessage.showError('This time slot overlaps with an existing booking. Please choose a different time.');
        setLoadingMessage('Refreshing calendar...');
        await fetchMonthlyEvents(currentYear, currentMonth);
        return;
      }
      
      // Handle specific race condition errors for tutor updates
      if (error.response && error.response.status) {
        try {
          const errorData = await error.response.json();
          if (errorData.errorCode) {            switch (errorData.errorCode) {              case 'BOOKING_STATE_CHANGED':
                statusMessage.showWarning('This availability slot has been modified by another session. The page will refresh to show the latest information.');
                setLoadingMessage('Refreshing calendar...');
                await fetchMonthlyEvents(currentYear, currentMonth);
                return;
              case 'STALE_DATA':
                statusMessage.showWarning('This slot was recently modified. The page will refresh to show the latest information.');
                setLoadingMessage('Refreshing calendar...');
                await fetchMonthlyEvents(currentYear, currentMonth);
                return;
              default:
                statusMessage.showError(`Error updating availability: ${errorData.message || 'Unknown error'}`);            }          } else {
            statusMessage.showError(`Error updating availability: ${errorData.message || 'Unknown error'}`);
          }
        } catch (parseError) {
          statusMessage.showError(`Error updating availability: ${error.message || 'Unknown error'}`);
        }
      } else {
        statusMessage.showError(`Error updating availability: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsAddingEvent(false);
      setIsLoading(false);
      setLoadingMessage('');
    }
  };  // Add this function to handle reservation
  const handleReserve = async (event, customStartTime = null, customEndTime = null, skipPaymentModal = false) => {
    if (!currentUser || !userDoc) {
      return;
    }

    // If user is a student and hasn't skipped the modal, check if we should show payment confirmation
    if (userType !== 'tutor' && !skipPaymentModal && !hidePaymentConfirmation) {
      // Set up session details for the modal
      setPaymentSessionDetails({
        startTime: customStartTime || event.startTime,
        endTime: customEndTime || event.endTime,
        date: event.date || event.bookingDate,
        tutorName: event.tutorName,
        description: event.text || event.description
      });
      
      // Store the action to be performed after confirmation
      setPendingPaymentAction({
        type: 'reserve',
        event: event,
        customStartTime: customStartTime,
        customEndTime: customEndTime
      });
      
      // Show the payment confirmation modal
      setShowPaymentConfirmModal(true);
      return;
    }

    // Set loading state
    setIsReserving(true);
    setIsLoading(true);
    setLoadingMessage('Reserving session...');
    
    // Only for non-tutors: check if payment methods exist
    if (userType !== 'tutor') {
        try {
          // Check if the user has a valid payment method
          const paymentMethodResponse = await api.post('/api/getPaymentMethods', {});
          
          if (!paymentMethodResponse.ok) {
            throw new Error('Failed to verify payment methods');
          }
          
          const paymentData = await paymentMethodResponse.json();
          
          if (!paymentData.paymentMethods || paymentData.paymentMethods.length === 0) {
            // No payment methods found, show payment methods section in dashboard
            setActivePage('paymentMethods');
            statusMessage.showWarning('Please add a payment method before reserving sessions.');
            setIsReserving(false);
            setIsLoading(false);
            setLoadingMessage('');
            return;
          }
          
          // Check if user has a default payment method
          if (!paymentData.paymentMethods.some(pm => pm.isDefault)) {
            setActivePage('paymentMethods');
            statusMessage.showWarning('Please set a default payment method before reserving sessions.');
            setIsReserving(false);
            setIsLoading(false);
            setLoadingMessage('');
            return;
          }
        } catch (error) {
          console.error('Error checking payment methods:', error);
          setIsReserving(false);
          setIsLoading(false);
          setLoadingMessage('');
          return;
        }
      }

      try {
        setLoadingMessage('Processing reservation...');
        // Prepare the request body
      const requestBody = {
        email: currentUser.email,
        studentName: userDoc.firstName && userDoc.lastName
          ? `${userDoc.firstName} ${userDoc.lastName}` 
          : (userDoc.name || currentUser.displayName || currentUser.email),
        studentEmail: currentUser.email,        studentConfirmed: true,
        type: 'reserved',
        // Add optimistic locking fields
        expectedType: event.type || 'available',
        lastKnownUpdate: event.updatedAt 
          ? (event.updatedAt._seconds 
            ? new Date(event.updatedAt._seconds * 1000).toISOString() 
            : event.updatedAt)
          : new Date().toISOString()
      };

      // Add partial reservation times if provided
      if (customStartTime && customEndTime) {
        requestBody.reservedStartTime = customStartTime;
        requestBody.reservedEndTime = customEndTime;        setLoadingMessage('Processing partial reservation...');
      }

      const response = await api.put(`/api/updateBooking/${event.id}`, requestBody);

      const data = await response.json();
      if (!response.ok) {
        // Handle specific race condition errors with user-friendly messages
        if (data.errorCode) {
          switch (data.errorCode) {            case 'BOOKING_STATE_CHANGED':
              statusMessage.showWarning('This time slot has been modified by another user. The page will refresh to show the latest availability.');
              setLoadingMessage('Refreshing calendar...');
              await fetchMonthlyEvents(currentYear, currentMonth);
              return;
            case 'STALE_DATA':
              statusMessage.showWarning('This booking was recently modified. The page will refresh to show the latest information.');
              setLoadingMessage('Refreshing calendar...');
              await fetchMonthlyEvents(currentYear, currentMonth);
              return;            case 'SLOT_NO_LONGER_AVAILABLE':
              statusMessage.showError('This time slot is no longer available. Please choose another time slot.');
              setLoadingMessage('Refreshing calendar...');
              await fetchMonthlyEvents(currentYear, currentMonth);
              return;
            case 'ALREADY_RESERVED_BY_ANOTHER_STUDENT':
              statusMessage.showError('This time slot has just been reserved by another student. Please choose a different time slot.');
              setLoadingMessage('Refreshing calendar...');
              await fetchMonthlyEvents(currentYear, currentMonth);
              return;
            case 'BOOKING_BEING_PROCESSED':
              statusMessage.showWarning('This booking is currently being processed by another user. Please wait a moment and try again.');
              setLoadingMessage('Refreshing calendar...');
              await fetchMonthlyEvents(currentYear, currentMonth);
              return;
            case 'SLOT_BEING_RESERVED':
              statusMessage.showWarning('Another user is currently reserving this slot. Please wait a moment and try again.');
              setLoadingMessage('Refreshing calendar...');
              await fetchMonthlyEvents(currentYear, currentMonth);
              return;
            case 'OVERLAPPING_PARTIAL_RESERVATION':
              statusMessage.showError('Another user is reserving an overlapping time slot. Please choose a different time range.');
              setLoadingMessage('Refreshing calendar...');
              await fetchMonthlyEvents(currentYear, currentMonth);
              return;
            case 'VERSION_MISMATCH':
              statusMessage.showWarning('This booking was modified while you were selecting your time. Please review the updated availability and try again.');
              setLoadingMessage('Refreshing calendar...');
              await fetchMonthlyEvents(currentYear, currentMonth);
              return;
            case 'BOOKING_TIME_CHANGED':
              statusMessage.showWarning('The time slot has been changed by the tutor. Please review the new times and try again.');
              setLoadingMessage('Refreshing calendar...');
              await fetchMonthlyEvents(currentYear, currentMonth);
              return;
            default:
              // Check for specific error messages that indicate slot modifications
              if (data.message && data.message.includes('Slot times have been modified')) {
                statusMessage.showWarning('The time slot was modified while you were making your selection. The calendar has been refreshed with the latest availability.');
                setLoadingMessage('Refreshing calendar...');
                await fetchMonthlyEvents(currentYear, currentMonth);
                return;
              } else if (data.message && data.message.includes('no longer available')) {
                statusMessage.showError('This slot is no longer available. It may have been reserved by another student or removed by the tutor.');
                setLoadingMessage('Refreshing calendar...');
                await fetchMonthlyEvents(currentYear, currentMonth);
                return;
              } else if (data.message && data.message.includes('Booking no longer exists')) {
                statusMessage.showError('This time slot has been removed by the tutor.');
                setLoadingMessage('Refreshing calendar...');
                await fetchMonthlyEvents(currentYear, currentMonth);
                return;
              }
              throw new Error(data.message || 'Failed to reserve session');
          }
        } else {
          throw new Error(data.message || 'Failed to reserve session');
        }
      }
      
      // Success case - refresh calendar
      setLoadingMessage('Refreshing calendar...');
      await fetchMonthlyEvents(currentYear, currentMonth);    } catch (error) {
      console.error('Reserve error:', error);
      statusMessage.showError(`Error reserving session: ${error.message}`);
    } finally {
      setIsReserving(false);
      setIsLoading(false);
      setLoadingMessage('');
    }
  };  // New function to handle session cancellation
  const handleCancelSession = async (bookingId, sessionData = null) => {
    if (!currentUser) return;

    try {
      // Find the booking to check user permissions
      let booking = sessionData || mySessions.find(b => b.id === bookingId) || events.find(b => b.id === bookingId);      if (!booking) {
        statusMessage.showError('Session not found. Please refresh the page and try again.');
        return;
      }

      // Check if user is authorized to cancel (tutor or student)
      const isAuthorized = booking.tutorEmail === currentUser.email || booking.studentEmail === currentUser.email;      if (!isAuthorized) {
        statusMessage.showError('You are not authorized to cancel this session.');
        return;
      }// Check if session has already started
      const sessionDate = booking.date || booking.bookingDate;
      let sessionDateTime = null;
      
      if (sessionDate && booking.startTime) {
        try {
          let date = sessionDate;
          if (sessionDate && typeof sessionDate === 'object' && sessionDate.seconds) {
            date = new Date(sessionDate.seconds * 1000);
          } else if (typeof sessionDate === 'string' || typeof sessionDate === 'number') {
            date = new Date(sessionDate);
          }
          
          // Parse start time (e.g., "1:00 PM")
          const [timePart, ampm] = booking.startTime.split(' ');
          const [hours, minutes] = timePart.split(':').map(Number);
          
          let hour24 = hours;
          if (ampm && ampm.toUpperCase() === 'PM' && hours < 12) {
            hour24 += 12;
          } else if (ampm && ampm.toUpperCase() === 'AM' && hours === 12) {
            hour24 = 0;
          }
          
          // Create a date object for the session start time
          sessionDateTime = new Date(date);
          sessionDateTime.setHours(hour24, minutes, 0, 0);
        } catch (parseError) {
          console.error('Error parsing session date/time:', parseError);
        }
      }        // Check if session has already started (can't cancel past sessions)
      if (sessionDateTime && sessionDateTime <= new Date()) {
        statusMessage.showError('Cannot cancel a session that has already started.');
        return;
      }

      // Check refund eligibility
      const currentTime = new Date();
      const twentyFourHoursFromNow = new Date(currentTime.getTime() + 24 * 60 * 60 * 1000);
      const eligibleForRefund = !sessionDateTime || sessionDateTime > twentyFourHoursFromNow;      // Generate confirmation message based on user type and policies
      let refundMessage = '';
      // Check if payment was processed (paymentStatus can be 'succeeded' or 'paid', and paymentIntentId exists)
      const paymentProcessed = booking.type === 'confirmed' && 
                               (booking.paymentStatus === 'succeeded' || booking.paymentStatus === 'paid') && 
                               booking.paymentIntentId;
      
      if (paymentProcessed) {
        if (userType === 'tutor') {
          // Tutors always trigger refunds when cancelling
          refundMessage = 'A full refund will be processed to the student within 5-10 business days.';
        } else {
          // Students only get refunds if cancelling 24+ hours in advance
          if (eligibleForRefund) {
            refundMessage = 'Since this session is being cancelled more than 24 hours in advance, a full refund will be processed to your original payment method within 5-10 business days.';
          } else {
            refundMessage = 'Since this session is being cancelled within 24 hours of the start time, no refund will be issued according to our student cancellation policy.';
          }
        }
      } else if (booking.type === 'confirmed') {
        // Confirmed but no payment processed yet (rare case)
        refundMessage = 'This session is confirmed but payment has not been processed yet. No charges will occur.';
      } else {
        // Reserved or other non-payment states
        refundMessage = 'No payment has been processed for this session, so no refund is needed.';
      }

      // Show confirmation modal instead of window.confirm
      confirmationModal.showConfirmation({
        title: 'Cancel Session',
        message: `Are you sure you want to cancel this session?\n\n${refundMessage}`,
        confirmText: 'Cancel Session',
        cancelText: 'Keep Session',
        type: 'danger',
        onConfirm: async () => {
          try {
            setIsLoading(true);
            setLoadingMessage('Cancelling session...');            // Generate cancellation token
            const tokenResponse = await api.post('/api/generateCancellationToken', { 
              bookingId 
            });

            if (!tokenResponse.ok) {
              throw new Error('Failed to generate cancellation token');
            }

            const tokenData = await tokenResponse.json();

            // Cancel the session using the token
            const cancelResponse = await api.post('/api/cancelSession', { token: tokenData.token });

            if (!cancelResponse.ok) {
              const errorData = await cancelResponse.json();
              
              // Handle specific race condition errors for cancellation
              if (errorData.errorCode) {
                switch (errorData.errorCode) {
                  case 'PAYMENT_IN_PROGRESS':
                    statusMessage.showWarning('Cannot cancel while payment is being processed. Please wait and try again.');
                    await fetchMonthlyEvents(currentYear, currentMonth);
                    return;
                  case 'CONFIRMATION_IN_PROGRESS':
                    statusMessage.showWarning('Cannot cancel while confirmation is in progress. Please wait and try again.');
                    await fetchMonthlyEvents(currentYear, currentMonth);
                    return;
                  default:
                    throw new Error(errorData.message || 'Failed to cancel session');
                }
              } else {
                throw new Error(errorData.message || 'Failed to cancel session');
              }
            }

            const cancelData = await cancelResponse.json();

            // Refresh data
            if (activePage === 'calendar') {
              await fetchMonthlyEvents(currentYear, currentMonth);
            } else if (activePage === 'mySessions') {
              await fetchMySessions();
            }            // Show success message
            if (cancelData.refund) {
              // Refund processed
            } else {
              // Session cancelled without refund
            }          } catch (error) {
            console.error('Cancellation error:', error);
            statusMessage.showError(`Error cancelling session: ${error.message}`);
          } finally {
            setIsLoading(false);
            setLoadingMessage('');
          }
        }
      });    } catch (error) {
      console.error('Cancellation error:', error);
      statusMessage.showError(`Error cancelling session: ${error.message}`);
    }
  };

  // Helper: get full name for search (case-insensitive, fallback to email)
  function getFullName(user) {
    if (!user) return '';
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    if (user.name) return user.name;
    return '';
  }

  // Helper: get tutor initials for profile photo placeholder
  const getTutorInitials = (tutorName, tutorEmail) => {
    if (tutorName) {
      const parts = tutorName.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
      }
      return tutorName.charAt(0).toUpperCase();
    }
    if (tutorEmail) {
      return tutorEmail.charAt(0).toUpperCase();
    }
    return 'T';
  };

  // Helper: get tutor profile data from allTutors array
  const getTutorProfile = (tutorEmail) => {
    if (!tutorEmail || !allTutors) return null;
    return allTutors.find(t => t.email === tutorEmail);
  };  // Tutor autocomplete (for students) -- enhanced version with better search
  const renderTutorAutocomplete = () => {
    if (userType === 'tutor') return null;
    
    // Filter tutors based on search input - improved search algorithm
    const filtered = tutorSearch.length > 0
      ? allTutors.filter(t => {
          const firstName = (t.firstName || '').toLowerCase();
          const lastName = (t.lastName || '').toLowerCase();
          const fullName = getFullName(t).toLowerCase();
          const email = (t.email || '').toLowerCase();
          const subjects = (t.subjects || []).map(s => s.toLowerCase());
          const search = tutorSearch.toLowerCase();
          
          // Check if search matches any part of name, email, or subjects
          return firstName.includes(search) || 
                 lastName.includes(search) ||
                 fullName.includes(search) ||
                 email.includes(search) ||
                 subjects.some(subject => subject.includes(search)) ||
                 // Also check if search matches start of words (better autocomplete experience)
                 firstName.startsWith(search) ||
                 lastName.startsWith(search) ||
                 email.startsWith(search);
        })
      : []; // Show no tutors when search is empty (better UX for autocomplete)
    
    return (
      <div className="tutor-autocomplete-container">        <input
          className="tutor-autocomplete-input"
          type="text"
          placeholder="Search Tutor Name or Email."
          value={selectedTutor ? (getFullName(selectedTutor) || selectedTutor.email) : tutorSearch}
          onChange={e => {
            setTutorSearch(e.target.value);
            setSelectedTutor(null);
            setShowTutorDropdown(true);
          }}onFocus={() => {
            setShowTutorDropdown(true);
          }}
          onBlur={(e) => {
            // Use relatedTarget to check if focus is moving to dropdown
            if (!e.relatedTarget || !e.currentTarget.parentNode.contains(e.relatedTarget)) {
              // Delay hiding dropdown to allow for clicks
              setTimeout(() => setShowTutorDropdown(false), 200);
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              setShowTutorDropdown(false);
              e.target.blur();
            }
          }}
          autoComplete="off"
        />        {(tutorSearch || selectedTutor) && (
          <button
            className="tutor-autocomplete-clear"
            onClick={() => {
              setSelectedTutor(null);
              setTutorSearch('');
              setShowTutorDropdown(false);
              // No loading needed - filtering is now client-side only
            }}
            title="Clear search"
          >
            ×
          </button>
        )}        {showTutorDropdown && tutorSearch && !selectedTutor && (
          <div className="tutor-autocomplete-dropdown">
            {filtered.length > 0 ? (
              filtered.map((tutor, idx) => (                <div
                  key={tutor.email || idx}
                  className={`tutor-autocomplete-option${selectedTutor && tutor.email === selectedTutor.email ? ' selected' : ''}`}
                  onClick={async () => {
                    setSelectedTutor(tutor);
                    setTutorSearch(getFullName(tutor) || tutor.email);
                    setShowTutorDropdown(false);
                    // Check for availability and auto-navigate if needed
                    await handleTutorSelection(tutor);
                  }}
                  style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                >
                  <div style={{ fontWeight: 'bold', color: '#333' }}>
                    {getFullName(tutor) || 'No name available'}
                  </div>
                  <div style={{ fontSize: '0.9em', color: '#666' }}>
                    {tutor.email}
                  </div>
                  {tutor.subjects && tutor.subjects.length > 0 && (
                    <div style={{ fontSize: '0.8em', color: '#888', marginTop: '2px' }}>
                      Teaches: {tutor.subjects.slice(0, 3).join(', ')}
                      {tutor.subjects.length > 3 && '...'}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="tutor-autocomplete-option" style={{ color: '#999', fontStyle: 'italic' }}>
                Enter a Tutor Name or Email
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderMySessions = () => {
    // Only treat 'reserved' as reserved, not 'pending'
    // Split sessions by type
    const reserved = mySessions.filter(b => b.type === 'reserved');
    const confirmed = mySessions.filter(b => b.type === 'confirmed');
    const tutorReserved = mySessions.filter(b => b.type === 'tutorReserved');
    // For tutors, show available slots (unbooked)
    const available = userType === 'tutor'
      ? mySessions.filter(b => b.type === 'available')
      : [];
    const monthsOfYear = [
      'January','February','March','April','May','June','July','August','September','October','November','December'
    ];    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '2rem' }}>
        <h2 style={{ marginBottom: '1.5rem' }}>My Sessions</h2>
        {sessionsLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div>Loading your sessions...</div>
          </div>
        ) : (
          <>
            {/* Available section for tutors */}
            {userType === 'tutor' && (
              <div style={{ marginBottom: '2rem' }}>
                <h3>Available</h3>
                {available.length === 0 && <div>No available slots.</div>}
                {available.map((b, idx) => {
                  const tutorProfile = getTutorProfile(b.tutorEmail);
                  return (
                    <div key={b.id} style={{
                      background: '#e2f4ea',
                      border: '1px solid #28a745',
                      borderRadius: 8,
                      padding: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                            {/* Tutor profile photo */}
                            {b.tutorEmail && (
                              <div style={{
                                flexShrink: 0,
                                width: '2.5rem',
                                height: '2.5rem',
                                borderRadius: '50%',
                                background: tutorProfile?.profileImage ? 'transparent' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.875rem',
                                fontWeight: 'bold',
                                overflow: 'hidden',
                                border: '2px solid #e2e8f0'
                              }}>
                                {tutorProfile?.profileImage ? (
                                  <img 
                                    src={tutorProfile.profileImage} 
                                    alt={b.tutorName || 'Tutor'} 
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                ) : (
                                  getTutorInitials(b.tutorName, b.tutorEmail)
                                )}
                              </div>
                            )}
                            <div>
                              <b>
                                {(() => {
                                  let d = b.date;
                                  if (!d && b.bookingDate) d = b.bookingDate;
                                  if (d && typeof d === 'object' && d.seconds) d = new Date(d.seconds * 1000);
                                  else if (typeof d === 'string' || typeof d === 'number') d = new Date(d);
                                  return d && !isNaN(d)
                                    ? `${monthsOfYear[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
                                    : 'Invalid date';
                                })()}
                              </b>
                              {' '}| {b.startTime} - {b.endTime}
                            </div>
                          </div>
                          <div>
                            {b.description || b.text || ''}
                          </div>                          <div style={{ fontSize: 13, color: '#333', marginTop: 4 }}>
                            Tutor: {formatParticipant(b.tutorName, b.tutorEmail)},
                            <br />
                            Student: {formatParticipant(b.studentName, b.studentEmail)}
                          </div>
                        </div>
                        <button
                          style={{
                            background: '#dc3545',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            padding: '0.5rem 1rem',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                          onClick={() => handleCancelSession(b.id, b)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ marginBottom: '2rem' }}>
              <h3>Reserved</h3>
              {[
                ...reserved,
                ...tutorReserved
              ].length === 0 && <div>No reserved sessions.</div>}              {[...reserved, ...tutorReserved].map((b, idx) => {
                // --- Determine if this is a tutorReserved event for a non-tutor ---
                const isTutorReservedForNonTutor = b.type === 'tutorReserved' && userType !== 'tutor';
                // --- Determine if this is a reserved event for a tutor ---
                const isReservedForTutor = b.type === 'reserved' && userType === 'tutor';
                const tutorProfile = getTutorProfile(b.tutorEmail);
                return (                  <div key={b.id} style={{
                    background: 'rgb(153, 202, 255)',
                    border: '1px solid rgb(13, 0, 159)',
                    borderRadius: 8,
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      {/* Tutor profile photo */}
                      {b.tutorEmail && (
                        <div style={{
                          flexShrink: 0,
                          width: '2.5rem',
                          height: '2.5rem',
                          borderRadius: '50%',
                          background: tutorProfile?.profileImage ? 'transparent' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          overflow: 'hidden',
                          border: '2px solid #e2e8f0'
                        }}>
                          {tutorProfile?.profileImage ? (
                            <img 
                              src={tutorProfile.profileImage} 
                              alt={b.tutorName || 'Tutor'} 
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          getTutorInitials(b.tutorName, b.tutorEmail)
                        )}
                        </div>
                      )}
                      <div>
                        <b>
                          {(() => {
                            let d = b.date;
                            if (!d && b.bookingDate) d = b.bookingDate;
                            if (d && typeof d === 'object' && d.seconds) d = new Date(d.seconds * 1000);
                            else if (typeof d === 'string' || typeof d === 'number') d = new Date(d);
                            return d && !isNaN(d)
                              ? `${monthsOfYear[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
                              : 'Invalid date';
                          })()}
                        </b>
                        {' '}| {b.startTime} - {b.endTime}
                      </div>
                    </div>
                    <div>
                      {b.description || b.text || ''}
                    </div>
                      {/* Inline layout for participant info and buttons */}
                                                                             <div className="event-participants-and-buttons">
                        <div className="event-participants" style={{ fontSize: 13, color: '#333', marginTop: 4 }}>
                          Tutor: {formatParticipant(b.tutorName, b.tutorEmail)}
                          <br />
                          Student: {formatParticipant(b.studentName, b.studentEmail)}
                        </div>
                      
                      <div className="event-inline-buttons">
                        {/* --- Show green Confirm button for tutorReserved for non-tutor OR reserved for tutor --- */}
                        {isTutorReservedForNonTutor || isReservedForTutor ? (
                          <button
                            className="session-button confirm"
                            onClick={() => handleConfirmSession(b.id)}
                          >
                            Confirm
                          </button>
                        ) : null}
                        <button
                          className="session-button cancel"
                          onClick={() => handleCancelSession(b.id, b)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div>
              <h3>Confirmed</h3>
              {confirmed.length === 0 && <div>No confirmed sessions.</div>}
              {confirmed.map((b, idx) => {
                const tutorProfile = getTutorProfile(b.tutorEmail);
                return (
                  <div key={b.id} style={{
                    background: 'rgb(236, 189, 235)',
                    border: '1px solid rgb(117, 8, 114)',
                    borderRadius: 8,
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      {/* Tutor profile photo */}
                      {b.tutorEmail && (
                        <div style={{
                          flexShrink: 0,
                          width: '2.5rem',
                          height: '2.5rem',
                          borderRadius: '50%',
                          background: tutorProfile?.profileImage ? 'transparent' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          overflow: 'hidden',
                          border: '2px solid #e2e8f0'
                        }}>
                          {tutorProfile?.profileImage ? (
                            <img 
                              src={tutorProfile.profileImage} 
                              alt={b.tutorName || 'Tutor'} 
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          getTutorInitials(b.tutorName, b.tutorEmail)
                        )}
                      </div>
                    )}
                    <div>
                      <b>
                        {(() => {
                          let d = b.date;
                          if (!d && b.bookingDate) d = b.bookingDate;
                          if (d && typeof d === 'object' && d.seconds) d = new Date(d.seconds * 1000);
                          else if (typeof d === 'string' || typeof d === 'number') d = new Date(d);
                          return d && !isNaN(d)
                            ? `${monthsOfYear[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
                            : 'Invalid date';
                                           })()}
                      </b>
                      {' '}| {b.startTime} - {b.endTime}
                    </div>
                  </div>
                  <div>
                    {b.description || b.text || ''}
                  </div>
                      {/* Inline layout for participant info and buttons */}
                    <div className="event-participants-and-buttons">
                      <div className="event-participants" style={{ fontSize: 13, color: '#333', marginTop: 4 }}>
                        Tutor: {formatParticipant(b.tutorName, b.tutorEmail)}
                        <br />
                        Student: {formatParticipant(b.studentName, b.studentEmail)}
                      </div>
                    
                      <div className="event-inline-buttons">
                        {/* Add Join Google Meet button for confirmed sessions */}
                        {b.meetLink && (
                          <button
                            className="session-button join-meet"
                            onClick={() => window.open(b.meetLink, '_blank', 'noopener,noreferrer')}
                            title="Join Google Meet session"
                          >
                            Join Meet
                          </button>
                        )}
                        <button
                          className="session-button cancel"
                          onClick={() => handleCancelSession(b.id, b)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  // Tutor autocomplete (for students)
  const renderStudentAutocomplete = () => {
    if (userType !== 'tutor') return null;
    const showDropdown = studentSearch.length > 0;
    const filtered = showDropdown
      ? allStudents.filter(s => {
                   // Better name handling for firstName/lastName combinations or just name field
          const firstName = (s.firstName || '').toLowerCase();
          const lastName = (s.lastName || '').toLowerCase();
          const fullName = `${firstName} ${lastName}`.trim();
          const nameField = (s.name || '').toLowerCase();
          const email = (s.email || '').toLowerCase();
          const search = studentSearch.toLowerCase();
          
          return firstName.includes(search) || 
                 lastName.includes(search) ||
                 fullName.includes(search) ||
                 nameField.includes(search) || 
                 email.includes(search);
        })
      : [];    return (
      <div className="student-autocomplete-container">
        <input
          className="tutor-autocomplete-input"
          type="text"
          placeholder="Search student name/email..."
          value={selectedStudent ? (getFullName(selectedStudent) || selectedStudent.email) : studentSearch}
          onChange={e => {
            setStudentSearch(e.target.value);
            setSelectedStudent(null);
          }}
          onFocus={() => setStudentSearch('')}
          autoComplete="off"
        />
        {selectedStudent && (
          <button
            className="tutor-autocomplete-clear"
            onClick={() => {
              setSelectedStudent(null);
              setStudentSearch('');
            }}
            title="Clear"
          >
            ×
          </button>
        )}
        {showDropdown && filtered.length > 0 && !selectedStudent && (
          <div className="tutor-autocomplete-dropdown">
            {filtered.map((student, idx) => (
              <div
                key={student.email || idx}
                className={`tutor-autocomplete-option${getFullName(student) === getFullName(selectedStudent) ? ' selected' : ''}`}
                onClick={() => {
                  setSelectedStudent(student);
                  setStudentSearch(getFullName(student) || student.email);
                }}
                style={{ whiteSpace: 'normal', wordBreak: 'break-word' }} // Added wrapping styles
              >
                {getFullName(student) || student.email}
                {student.email ? ` (${student.email})` : ''}
              </div>
            ))}
          </div>
        )}        <button
          style={{
            marginTop: 8,
            marginLeft: 0,
            background: selectedStudent ? '#4285F4' : '#ccc',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            padding: '0.5rem 1rem',
            fontWeight: 'bold',
            fontSize: '1rem',
            cursor: (selectedStudent && !isLoading) ? 'pointer' : 'not-allowed',
            opacity: isLoading ? 0.7 : 1
          }}
          disabled={!selectedStudent || isLoading}
          onClick={() => {
            setStudentEventDate(selectedDate);
            setShowStudentEventPopup(true);
          }}
        >
          {isLoading ? 'Loading...' : 'Create Student Session'}
        </button>
      </div>
    );
  };

  const [studentEventText, setStudentEventText] = useState('');
  const [studentStartTime, setStudentStartTime] = useState('12:00');
  const [studentStartAmPm, setStudentStartAmPm] = useState('AM');
  const [studentEndTime, setStudentEndTime] = useState('12:15');
  const [studentEndAmPm, setStudentEndAmPm] = useState('AM');
  const [studentEventDate, setStudentEventDate] = useState(selectedDate || currentDate);

  // Helper to get YYYY-MM-DD for local date (for input[type=date])
  const getLocalDateInputValue = (dateObj) => {
    const d = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
    return d.toISOString().slice(0, 10);
  };
  const handleStudentEventSubmit = async () => {
    if (!selectedStudent) {
      statusMessage.showError('Please select a student');
      return;
    }
    
    // Convert times to minutes for comparison
    const startMinutes = convertTimeToMinutes(studentStartTime, studentStartAmPm);
    const endMinutes = convertTimeToMinutes(studentEndTime, studentEndAmPm);
    
    // Check if end time is before start time
    if (endMinutes <= startMinutes) {
      statusMessage.showError('End time must be after start time');
      return;
    }
    
    // Check if session is at least 30 minutes long
    if (endMinutes - startMinutes < 30) {
      statusMessage.showError('Sessions must be at least 30 minutes long');
      return;
    }
    
    if (!dayRollOverCheck(studentStartTime, studentStartAmPm, studentEndTime, studentEndAmPm)) {
      statusMessage.showError('Sessions cannot span across midnight');
      return;
    }

    setIsLoading(true);
    setLoadingMessage('Creating student session...');

    try {
      // Always use local midnight ISO string for the selected date
      await api.post('/api/addAvailability', {
          email: currentUser.email,
          date: toLocalMidnightISOString(studentEventDate),
          startTime: `${studentStartTime} ${studentStartAmPm}`,
          endTime: `${studentEndTime} ${studentEndAmPm}`,
          text: studentEventText,
          type: 'tutorReserved',
          studentName: getFullName(selectedStudent) || selectedStudent.email || '',
          studentEmail: selectedStudent.email
        });
      
      setLoadingMessage('Refreshing sessions...');
      setShowStudentEventPopup(false);
      setSelectedStudent(null);
      setStudentSearch('');
      setStudentEventText('');
      setStudentStartTime('12:00');
      setStudentStartAmPm('AM');
      setStudentEndTime('12:15');
      setStudentEndAmPm('AM');
      // Don't reset studentEventDate to preserve calendar selection
      // Refresh both sessions and monthly calendar data
      await fetchMonthlyEvents(currentYear, currentMonth);
    } catch (error) {
      console.error('Error creating student session:', error);
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };
  
  // Helper function to validate partial reservation time is within available slot
  const isValidPartialReservationTime = (slotStartTime, slotEndTime, customStartTime, customEndTime) => {
    try {
      const slotStartMinutes = convertFullTimeToMinutes(slotStartTime);
      const slotEndMinutes = convertFullTimeToMinutes(slotEndTime);
      const customStartMinutes = convertFullTimeToMinutes(customStartTime);
      const customEndMinutes = convertFullTimeToMinutes(customEndTime);

      return customStartMinutes >= slotStartMinutes && 
             customEndMinutes <= slotEndMinutes &&
             customStartMinutes < customEndMinutes;
    } catch (error) {
      console.error('Error validating partial reservation time:', error);
      return false;
    }  };
  
  // Helper function to get available time options within a slot
  const getAvailableTimesForSlot = (slotStartTime, slotEndTime) => {
    const slotStartMinutes = convertFullTimeToMinutes(slotStartTime);
    const slotEndMinutes = convertFullTimeToMinutes(slotEndTime);
    
    return fullTimeIncrements.filter(time => {
      const timeMinutes = convertFullTimeToMinutes(time);
      return timeMinutes >= slotStartMinutes && timeMinutes <= slotEndMinutes;
    });
  };
    // Helper function to initialize partial reservation modal with slot's full time
  const openPartialReservationModal = (event) => {
    setSelectedSlotForPartialReservation(event);
    
    // Get available times within the slot
    const availableTimes = getAvailableTimesForSlot(event.startTime, event.endTime);
    
    // Set start time to the earliest available time (slot start time)
    const startTime = availableTimes.length > 0 ? availableTimes[0] : event.startTime;
    
    // Find the earliest valid end time that's at least 30 minutes after start time
    const startMinutes = convertFullTimeToMinutes(startTime);
    const minEndMinutes = startMinutes + 30; // Minimum 30 minutes session
    
    // Find the first time that's at least 30 minutes after start
    const validEndTime = availableTimes.find(time => {
      const timeMinutes = convertFullTimeToMinutes(time);
      return timeMinutes >= minEndMinutes;
    });
    
    // If no valid end time found (slot is less than 30 minutes), use the latest time
    // but validation will catch this and show an error
    const endTime = validEndTime || (availableTimes.length > 0 ? availableTimes[availableTimes.length - 1] : event.endTime);
    
    setPartialStartTime(startTime);
    setPartialEndTime(endTime);
    setPartialTimeValidationMessage(''); // Clear any previous validation messages
    
    setShowPartialReservationModal(true);
  };

  // Function to handle tutor selection and auto-navigate to availability
  const handleTutorSelection = async (tutor) => {
    if (!tutor) return;
    
    // Helper function to check if a tutor has availability in given month data
    const tutorHasAvailability = (monthData, tutorToCheck) => {
      if (!monthData || monthData.length === 0) return false;
      
      return monthData.some(event => {
        // Only check available sessions
        if (event.type !== 'available') return false;
        
        // Check if this event belongs to the selected tutor
        const tutorEmailMatch = event.tutorEmail && event.tutorEmail === tutorToCheck.email;
        const tutorNameMatch = event.tutorName && event.tutorName === (tutorToCheck.name || getFullName(tutorToCheck));
        
        return tutorEmailMatch || tutorNameMatch;
      });
    };

    // Check current month first
    const currentMonthHasAvailability = tutorHasAvailability(monthlyEvents, tutor);
    
    if (currentMonthHasAvailability) {
      // Tutor has availability in current month, no need to navigate
      return;
    }

    // No availability in current month, search next 3 months
    
    let searchMonth = currentMonth;
    let searchYear = currentYear;
    let foundAvailabilityMonth = null;
    
    for (let i = 1; i <= 3; i++) {
      // Move to next month
      searchMonth = searchMonth === 11 ? 0 : searchMonth + 1;
      if (searchMonth === 0) {
        searchYear++;
      }
      
      try {
        // Fetch data for this month to check availability
        let response;
        if (userType === 'tutor') {
          response = await api.post('/api/getTutorBookingsForMonth', {
              email: currentUser.email,
              year: searchYear,
              month: searchMonth
            });        } else {
          response = await api.post('/api/getStudentBookingsForMonth', {
              year: searchYear,
              month: searchMonth
            });
        }
        
        if (response.ok) {
          const data = await response.json();
          const monthData = data.bookings || [];
          
          if (tutorHasAvailability(monthData, tutor)) {
            foundAvailabilityMonth = { month: searchMonth, year: searchYear };
            break;
          }
        }
      } catch (error) {
        console.error(`Error checking availability for ${monthsOfYear[searchMonth]} ${searchYear}:`, error);
      }
    }
      if (foundAvailabilityMonth) {
      // Navigate to the month with availability using smooth animation
      
      // Determine animation direction based on month difference
      const currentMonthIndex = currentYear * 12 + currentMonth;
      const targetMonthIndex = foundAvailabilityMonth.year * 12 + foundAvailabilityMonth.month;
      const direction = targetMonthIndex > currentMonthIndex ? 'right' : 'left';
        // Use animation for navigation
      setIsNavigating(true);
      setIsNavigatingMonth(true); // Set month navigation flag for auto-navigation
      setNavigationDirection(direction);
        // Small delay to let animation start
      setTimeout(() => {
        setCurrentMonth(foundAvailabilityMonth.month);
        setCurrentYear(foundAvailabilityMonth.year);
        
        // Show blue highlight briefly after transition
        setShowMonthHighlight(true);
        setTimeout(() => {
          setShowMonthHighlight(false);
        }, 800); // Blue highlight duration
        
        // Reset animation state after transition
        setTimeout(() => {
          setIsNavigating(false);
          setIsNavigatingMonth(false); // Clear month navigation flag
          setNavigationDirection('');
        }, 300); // Duration matches CSS animation
      }, 150);
      
      // The useEffect hook will automatically fetch the new month's data
      // No need to manually call fetchMonthlyEvents here
    } else {
      // Show error message to user
      const tutorName = getFullName(tutor) || tutor.email || 'this tutor';
      statusMessage.showError(`No availability found for ${tutorName} for at least the next 3 months`);
    }
  };

  // ...existing code...
  const renderContent = () => {
    switch(activePage) {
      case 'information':
        return <MyInformation />;
      case 'mySessions':
        return renderMySessions();
      case 'paymentMethods':
        return <PaymentMethods initialMessage={paymentPageMessage} onMessageShown={() => setPaymentPageMessage(null)} />;
      case 'stripeConnect':
        return <StripeConnectSetup />;
      case 'calendar':
      default:
        return (
          <div className='booking-container-wrapper'>
            <div className='dashboard-layout'>
              <div className="dashboard-features-col">
                {/* Only show filtering to non-tutors */}
                {userType !== 'tutor' && renderTutorAutocomplete()}                {userType !== 'tutor' && (
                  <div className="subject-filter-container">
                    <fieldset className="subject-filter-fieldset">
                      <legend className="subject-filter-legend">
                        Filter by Subject
                      </legend>
                      <div className="subject-filter-grid">
                        {subjectOptions.map((subject) => (
                          <label
                            key={subject}
                            className={`subject-filter-label ${pendingSubjectFilter.includes(subject) ? 'selected' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={pendingSubjectFilter.includes(subject)}
                              onChange={() => handleSubjectFilterChange(subject)}
                              className="subject-filter-checkbox"
                            />
                            <span className="subject-filter-text">
                              {subject.charAt(0).toUpperCase() + subject.slice(1)}
                            </span>
                          </label>
                        ))}
                      </div>                      {/* Apply/Reset buttons for subject filters */}
                      <div className={`subject-filter-actions ${(hasUnappliedSubjectChanges || pendingSubjectFilter.length > 0 || subjectFilter.length > 0) ? 'show' : ''}`} style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                        {hasUnappliedSubjectChanges && (
                          <button
                            className={`apply-filters-btn ${isLoading ? 'btn-loading' : ''}`}
                            onClick={applySubjectFilters}
                            disabled={isLoading}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: isLoading ? '#ccc' : '#4CAF50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: isLoading ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              opacity: isLoading ? 0.7 : 1
                            }}
                          >
                            {isLoading ? 'Applying...' : 'Apply Filters'}
                          </button>
                        )}
                        {(pendingSubjectFilter.length > 0 || subjectFilter.length > 0) && (
                          <button
                            className="reset-filters-btn"
                            onClick={resetSubjectFilters}
                            disabled={isLoading}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: isLoading ? '#ccc' : '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: isLoading ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              opacity: isLoading ? 0.7 : 1
                            }}
                          >
                            {isLoading ? 'Clearing...' : 'Clear All'}
                          </button>
                        )}
                      </div>{hasUnappliedSubjectChanges && (
                        <div>
                        </div>
                      )}
                      
                      {/* Show current active filters */}
                      {subjectFilter && subjectFilter.length > 0 && (
                        <div style={{ 
                          marginTop: '10px', 
                          padding: '8px', 
                          backgroundColor: '#d4edda', 
                          border: '1px solid #c3e6cb', 
                          borderRadius: '4px',
                          fontSize: '12px',
                          color: '#155724',
                          textAlign: 'center'
                        }}>
                          Active filters: {subjectFilter.join(', ')}
                        </div>
                      )}
                    </fieldset>
                  </div>
                )}
                {/* Tutor: show student autocomplete for tutorReserved */}
                {userType === 'tutor' && renderStudentAutocomplete()}
              </div>              <div className={`Calendar ${isNavigating ? 'calendar-navigating' : ''}`}>
                <div className="calendar-header">
                                   {userType === 'tutor' && (
                    <button 
                      className={`add-availability-btn ${isLoading || isNavigating ? 'btn-loading' : ''}`}
                      onClick={handleAddAvailability}
                      disabled={isLoading || isNavigating}
                    >
                      <i className='bx bx-plus'></i>
                      {isLoading || isNavigating ? 'Loading...' : 'Add Availability'}
                    </button>
                  )}
                </div><div className="calendar-date-navigation">
                  <i className={`bx bx-chevron-left nav-arrow nav-arrow-left ${isNavigating ? 'nav-disabled' : ''}`} 
                     onClick={prevMonth}></i>                  <div className={`date-display ${isNavigating ? `calendar-slide-${navigationDirection}` : ''}`}>
                    <h2 className={`month ${showMonthHighlight ? 'month-highlight' : ''}`}>{monthsOfYear[currentMonth]}</h2>
                    <h2 className="year">{currentYear}</h2>
                  </div>
                  <i className={`bx bx-chevron-right nav-arrow nav-arrow-right ${isNavigating ? 'nav-disabled' : ''}`} 
                     onClick={nextMonth}></i>
                </div>                <div className={`weekdays ${isNavigating ? `weekdays-slide-${navigationDirection}` : ''}`}>
                  {daysOfWeek.map((day) => (<span key={day}>{day}</span>))}
                </div>
                
                {/* Calendar days container with navigation overlay */}
                <div className="calendar-days-container">
                  {isNavigating && (
                    <div className="calendar-days-loading-overlay">
                      <div className="navigation-spinner">
                        <i className='bx bx-loader-alt bx-spin'></i>
                        <span>Loading calendar...</span>
                      </div>
                    </div>
                  )}
                  <div className={`days ${isNavigating ? `calendar-grid-slide-${navigationDirection}` : ''}`}>
                  {[...Array(firstDayOfMonth).keys()].map((_, index) => (
                    <span
                      key={`empty-${index}`}
                      className='disabled-day'
                    >
                      &nbsp;
                    </span>
                  ))}                {[...Array(daysInMonth).keys()].map((day) => {
                  const dayDate = new Date(currentYear, currentMonth, day + 1);
                  const isToday = day + 1 === currentDate.getDate() 
                    && currentMonth === currentDate.getMonth() 
                    && currentYear === currentDate.getFullYear();
                  const isSelectedDay = isSameDay(dayDate, selectedDate);
                    // Check for session status glows with proper priority system
                  const hasConfirmedSessions = dayHasConfirmedSessions(dayDate);
                  const hasReservedSessions = !hasConfirmedSessions && dayHasReservedSessions(dayDate);
                  
                  // For available sessions, prioritize them when any filter is active (tutor or subject)
                  const hasActiveFilters = (userType !== 'tutor' && selectedTutor !== null) || 
                                          (userType !== 'tutor' && subjectFilter && subjectFilter.length > 0);
                  const hasFilteredAvailableSessions = hasActiveFilters 
                    ? dayHasFilteredAvailableSessions(dayDate) // When filtering: show available as priority
                    : !hasConfirmedSessions && !hasReservedSessions && dayHasFilteredAvailableSessions(dayDate); // Normal priority: confirmed > reserved > available
                  
                  return (
                    <span 
                      key={day + 1} 
                      className={`
                        ${isToday ? 'today-text' : ''}
                        ${isSelectedDay ? 'selected-day' : ''}                        ${hasConfirmedSessions ? 'day-confirmed-sessions' : ''}
                        ${hasReservedSessions ? 'day-reserved-sessions' : ''}
                        ${hasFilteredAvailableSessions ? 'day-filtered-available-sessions' : ''}
                      `.trim()}
                      onClick={() => handleDayClick(day + 1)}
                    >                      {day + 1}
                    </span>
                  );
                })}
                  </div>
                </div>

              <div className = "events">
                {showEventPopup && (
                  <div className="popup-overlay">
                    <div className="event-popup">
                      <div className="selected-date-header">
                        {`${monthsOfYear[selectedDate.getMonth()]} ${selectedDate.getDate()}, ${selectedDate.getFullYear()}`}
                      </div>
                      <div className='time-input-container'>
                        <div className='event-popup-time'>Start Time:</div>
                        <div className='time-group'>
                          <select
                            className="time-select"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                          >
                            {timeIncrements.map((time) => (
                              <option key={time} value={time}>{time}</option>
                            ))}
                          </select>
                          <select
                            className="ampm-select"
                            value={startAmPm}
                            onChange={(e) => {
                              setStartAmPm(e.target.value);
                              if (!isValidTimeRange(startTime, e.target.value, endTime, endAmPm)) {
                                setEndTime(startTime);
                                setEndAmPm(e.target.value);
                              }
                            }}
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                      </div>

                      <div className='time-input-container'>
                        <div className='event-popup-time'>End Time:</div>
                        <div className='time-group'>
                          <select
                            className="time-select"
                            value={endTime}
                            onChange={(e) => setEndTime(e.target.value)}
                          >
                            {timeIncrements.map((time) => (
                              <option key={time} value={time}>{time}</option>
                            ))}
                          </select>
                          <select
                            className="ampm-select"
                            value={endAmPm}
                            onChange={(e) => {
                              if (isValidTimeRange(startTime, startAmPm, endTime, e.target.value)) {
                                setEndAmPm(e.target.value);
                              }
                            }}
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                      </div>

                      <textarea placeholder='Meeting Notes (optional, max 60 characters)' 
                      value ={eventText} onChange={e =>{
                        if(e.target.value.length <= 60){
                          setEventText(e.target.value);
                        }
                      }}></textarea>
                      <div style={{ margin: '0.5rem 0', textAlign: 'left' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                          <input
                            type="checkbox"
                            checked={recurringWeekly}
                            onChange={e => {
                              setRecurringWeekly(e.target.checked);
                              if (!e.target.checked) {
                                setRecurringWeeks(4); // Reset to default when unchecked
                              }
                            }}
                            style={{ marginRight: '0.5rem' }}
                          />
                          Recurring Weekly
                        </label>
                        {recurringWeekly && (
                          <div style={{ marginLeft: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <label>Number of weeks:</label>
                            <input
                              type="number"
                              min="1"
                              max="52"
                              value={recurringWeeks}
                              onChange={e => {
                                const value = parseInt(e.target.value) || 1;
                                setRecurringWeeks(Math.min(52, Math.max(1, value)));
                              }}
                              style={{ width: '60px', padding: '4px' }}
                            />
                            <span style={{ fontSize: '0.9em', color: '#666' }}>({recurringWeeks} weeks from selected date)</span>
                          </div>
                        )}
                      </div>                      <button 
                        className={`event-popup-btn ${isLoading ? 'btn-loading' : ''}`} 
                        onClick={handleEventSubmit}
                        disabled={isLoading}
                      >
                        {isLoading 
                          ? (editingEvent ? "Updating..." : "Adding...") 
                          : (editingEvent ? "Update event" : "Add Event")
                        }
                      </button>
                      <button 
                        className='close-event' 
                        onClick={() => !isLoading && setShowEventPopup(false)}
                        disabled={isLoading}
                      >
                        <i className='bx bx-x'></i>
                      </button>
                    </div>
                  </div>
                )}
                {showStudentEventPopup && (
                  <div className="popup-overlay">
                    <div className="event-popup">
                      <div className="selected-date-header">
                        <input
                          type="date"
                          value={getLocalDateInputValue(studentEventDate)}
                          onChange={e => {
                            // Always interpret as local date
                            const [year, month, day] = e.target.value.split('-').map(Number);
                            setStudentEventDate(new Date(year, month - 1, day));
                          }}
                          style={{ fontSize: '1.1rem', marginBottom: 8 }}
                        />
                      </div>
                      <div className='time-input-container'>
                        <div className='event-popup-time'>Start Time:</div>
                        <div className='time-group'>
                          <select
                            className="time-select"
                            value={studentStartTime}
                            onChange={(e) => setStudentStartTime(e.target.value)}
                          >
                            {timeIncrements.map((time) => (
                              <option key={time} value={time}>{time}</option>
                            ))}
                          </select>
                          <select
                            className="ampm-select"
                            value={studentStartAmPm}
                            onChange={(e) => {
                              setStudentStartAmPm(e.target.value);
                              if (!isValidTimeRange(studentStartTime, e.target.value, studentEndTime, studentEndAmPm)) {
                                setStudentEndTime(studentStartTime);
                                setStudentEndAmPm(e.target.value);
                              }
                            }}
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                      </div>
                      <div className='time-input-container'>
                        <div className='event-popup-time'>End Time:</div>
                        <div className='time-group'>
                          <select
                            className="time-select"
                            value={studentEndTime}
                            onChange={(e) => setStudentEndTime(e.target.value)}
                          >
                            {timeIncrements.map((time) => (
                              <option key={time} value={time}>{time}</option>
                            ))}
                          </select>
                          <select
                            className="ampm-select"
                            value={studentEndAmPm}
                            onChange={(e) => {
                              if (isValidTimeRange(studentStartTime, studentStartAmPm, studentEndTime, e.target.value)) {
                                setStudentEndAmPm(e.target.value);
                              }
                            }}
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                      </div>
                      <textarea
                        placeholder='Enter Event Text (Max 60 characters)'
                        value={studentEventText}
                        onChange={e => {
                          if (e.target.value.length <= 60) setStudentEventText(e.target.value);
                        }}
                      ></textarea>                      <button 
                        className={`event-popup-btn ${isLoading ? 'btn-loading' : ''}`} 
                        onClick={handleStudentEventSubmit}
                        disabled={isLoading}
                      >
                        {isLoading ? 'Creating...' : 'Create Session'}
                      </button>
                      <button 
                        className='close-event' 
                        onClick={() => !isLoading && setShowStudentEventPopup(false)}
                        disabled={isLoading}
                      >
                        <i className='bx bx-x'></i>
                      </button>
                    </div>
                  </div>                )}
                  {/* Partial Reservation Modal */}
                {showPartialReservationModal && selectedSlotForPartialReservation && (
                  <div className="popup-overlay">                    <div className="event-popup">
                      {/* Close button */}
                      <button 
                        className='close-event' 
                        onClick={() => {
                          if (!isLoading) {
                            setShowPartialReservationModal(false);
                            setSelectedSlotForPartialReservation(null);
                          }
                        }}
                        disabled={isLoading}
                      >
                        <i className='bx bx-x'></i>
                      </button>

                      <div style={{
                        textAlign: 'center',
                        padding: '1rem 0',
                      }}>
                        <h3 style={{
                          margin: 0,
                          fontSize: '1.8rem',
                          fontWeight: '600',
                          color: '#1565c0',
                          letterSpacing: '0.5px'
                        }}>
                          Select Preferred Time
                        </h3>
                        <p style={{
                          margin: '0.5rem 0 0 0',
                          fontSize: '0.9rem',
                          color: '#666',
                          fontStyle: 'italic'
                        }}>
                          Reserving Time With {selectedSlotForPartialReservation.tutorName}
                        </p>
                      </div>
                      <div style={{ 
                        marginBottom: '1rem', 
                        padding: '0.75rem', 
                        backgroundColor: '#e3f2fd', 
                        borderRadius: '4px',
                        border: '1px solid #90caf9'
                      }}>                        <div style={{ fontWeight: 'bold', color: '#1565c0' }}>
                          {(() => {
                            try {
                              let d = selectedSlotForPartialReservation.date || selectedSlotForPartialReservation.bookingDate;
                              if (d && typeof d === 'object' && d.toDate) {
                                d = d.toDate();
                              } else if (d && typeof d === 'object' && d.seconds) {
                                d = new Date(d.seconds * 1000);
                              } else if (typeof d === 'string' || typeof d === 'number') {
                                d = new Date(d);
                              }
                                if (d && !isNaN(d)) {
                                return `${monthsOfYear[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
                              } else {
                                return 'Invalid date';
                              }                            } catch (error) {
                              console.error('Error formatting date:', error);
                              return 'Date formatting error';
                            }
                          })()}: &nbsp;&nbsp;&nbsp;{selectedSlotForPartialReservation.startTime} - {selectedSlotForPartialReservation.endTime}
                        </div>
                      </div>                      <div className='time-input-container'>
                        <div className='event-popup-time'>Start Time:</div>
                        <div className='time-group'>                          <select
                            className="time-select"
                            value={partialStartTime}                            onChange={(e) => {
                              const selectedTime = e.target.value;
                              setPartialStartTime(selectedTime);
                              
                              // Only perform validation and auto-adjustment if the selected time is valid
                              const availableTimes = getAvailableTimesForSlot(selectedSlotForPartialReservation.startTime, selectedSlotForPartialReservation.endTime);
                              
                              if (availableTimes.includes(selectedTime)) {
                                // Auto-adjust end time if current end time is too close to new start time
                                const startMinutes = convertFullTimeToMinutes(selectedTime);
                                const endMinutes = convertFullTimeToMinutes(partialEndTime);
                                const minEndMinutes = startMinutes + 30;
                                
                                if (endMinutes < minEndMinutes) {
                                  // Find the earliest valid end time
                                  const validEndTime = availableTimes.find(time => {
                                    const timeMinutes = convertFullTimeToMinutes(time);
                                    return timeMinutes >= minEndMinutes;
                                  });
                                  
                                  if (validEndTime) {
                                    setPartialEndTime(validEndTime);
                                    setPartialTimeValidationMessage('');
                                  } else {
                                    setPartialTimeValidationMessage('Not enough time available for a 30-minute session with this start time.');
                                  }
                                } else {
                                  setPartialTimeValidationMessage('');
                                }
                              } else {
                                // Clear validation message when selecting from dropdown
                                setPartialTimeValidationMessage('');
                              }
                            }}
                          >
                            {(() => {
                              const availableTimes = getAvailableTimesForSlot(selectedSlotForPartialReservation.startTime, selectedSlotForPartialReservation.endTime);
                              
                              // Show all available times within the slot for start time selection
                              return availableTimes.map((time) => (
                                <option key={time} value={time}>{time}</option>
                              ));
                            })()}
                          </select>
                        </div>
                      </div>                      <div className='time-input-container'>
                        <div className='event-popup-time'>End Time:</div>
                        <div className='time-group'>                          <select
                            className="time-select"
                            value={partialEndTime}                            onChange={(e) => {
                              const selectedTime = e.target.value;
                              setPartialEndTime(selectedTime);
                              
                              // Only perform validation and auto-adjustment if the selected time is valid
                              const availableTimes = getAvailableTimesForSlot(selectedSlotForPartialReservation.startTime, selectedSlotForPartialReservation.endTime);
                              
                              if (availableTimes.includes(selectedTime)) {
                                // Auto-adjust start time if current start time is too close to new end time
                                const endMinutes = convertFullTimeToMinutes(selectedTime);
                                const startMinutes = convertFullTimeToMinutes(partialStartTime);
                                const maxStartMinutes = endMinutes - 15;
                                
                                if (startMinutes > maxStartMinutes) {
                                  // Find the latest valid start time
                                  const validStartTimes = availableTimes.filter(time => {
                                    const timeMinutes = convertFullTimeToMinutes(time);
                                    return timeMinutes <= maxStartMinutes;
                                  });
                                  
                                  if (validStartTimes.length > 0) {
                                    setPartialStartTime(validStartTimes[validStartTimes.length - 1]);
                                    setPartialTimeValidationMessage('');
                                  } else {
                                    setPartialTimeValidationMessage('Not enough time available for a 30-minute session with this end time.');
                                  }
                                } else {
                                  setPartialTimeValidationMessage('');
                                }                              } else {
                                // Clear validation message when selecting from dropdown
                                setPartialTimeValidationMessage('');
                              }
                            }}
                          >
                            {(() => {
                              const availableTimes = getAvailableTimesForSlot(selectedSlotForPartialReservation.startTime, selectedSlotForPartialReservation.endTime);
                              
                              // Show all available times within the slot for end time selection
                              return availableTimes.map((time) => (
                                <option key={time} value={time}>{time}</option>
                              ));
                            })()}
                          </select></div>
                      </div>
                    
                      {/* Validation Message */}
                      {partialTimeValidationMessage && (
                        <div style={{
                          color: '#d32f2f',
                          fontSize: '0.875rem',
                          marginTop: '0.5rem',
                          padding: '0.5rem',
                          backgroundColor: '#ffebee',
                          borderRadius: '4px',
                          border: '1px solid #ffcdd2'
                        }}>
                          {partialTimeValidationMessage}
                        </div>
                      )}
                    
                      
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>                        <button 
                          className={`event-popup-btn ${isLoading ? 'btn-loading' : ''}`}                          onClick={async () => {                            if (!isValidFullTimeRange(partialStartTime, partialEndTime)) {
                              setPartialTimeValidationMessage('End time must be at least 30 minutes after start time.');
                              return;
                            }
                            
                            // Validate that the custom time is within the available slot
                            const isTimeWithinSlot = isValidPartialReservationTime(
                              selectedSlotForPartialReservation.startTime,
                              selectedSlotForPartialReservation.endTime,
                              partialStartTime,
                              partialEndTime
                            );
                            
                            if (!isTimeWithinSlot) {
                              setPartialTimeValidationMessage('Your selected time must be within the available slot.');
                              return;
                            }
                            
                            setPartialTimeValidationMessage(''); // Clear any validation messages
                            setShowPartialReservationModal(false);
                            await handleReserve(selectedSlotForPartialReservation, partialStartTime, partialEndTime);
                          }}
                          disabled={isLoading}
                        >
                          {isLoading ? 'Reserving...' : 'Reserve Time Slot'}
                        </button>
                      </div>
                    </div>
                  </div>                )}
                  {/* Events container with animation */}
                <div className={`events-container ${
                  animationPhase === 'folding-in' ? 'events-fold-in' :
                  animationPhase === 'folding-out' ? 'events-fold-out' : ''
                } ${isNavigating ? `events-navigation-${navigationDirection}` : ''}`}>
                  {filteredEvents.map((event, index) => {
                    const eventDate = new Date(event.date);
                    const isTutor = userType === 'tutor';
                    // --- treat tutorReserved as reserved for tutors, but as confirmable for non-tutors ---
                    const eventType = (isTutor && event.type === 'tutorReserved') ? 'reserved' : (event.type || 'available');
                    const isTutorReserved = event.type === 'tutorReserved';
                                     const isReserved = eventType === 'reserved';
                    const isConfirmed = eventType === 'confirmed';
                    // Allow reserve if not tutor, not reserved, and not already booked
                    const canReserve = !isTutor && !isReserved && !isConfirmed && (!event.studentName || event.studentName === '');                    // Show confirm button for non-tutors viewing tutorReserved events OR tutors viewing reserved events
                    const showConfirmButton = (!isTutor && isTutorReserved) || (isTutor && event.type === 'reserved');                    const tutorProfile = getTutorProfile(event.tutorEmail);
                    return (                      <div className={`event ${
                        animationPhase === 'folding-out' ? 'event-animate' : ''
                      }`} key={event.id} style={{ position: 'relative' }}>
                      {/* Top right edit/delete buttons for tutors */}
                      {isTutor && (
                        <div className="event-top-right-buttons">
                          {/* Only show edit button for available slots (not confirmed, reserved, or tutorReserved) */}
                          {!isConfirmed && !isReserved && !isTutorReserved && (
                            <i 
                              className={`bx bxs-edit-alt ${isLoading ? 'loading-disabled' : ''}`} 
                              onClick={() => !isLoading && handleEventEdit(event)}
                              style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
                              title="Edit event"
                            ></i>
                          )}
                          <i 
                            className={`bx bxs-message-alt-x ${isLoading ? 'loading-disabled' : ''}`} 
                            onClick={() => {
                              if (!isLoading) {
                                // For reserved and confirmed sessions, use cancel instead of delete
                                if (isReserved || isConfirmed) {
                                  handleCancelSession(event.id, event);
                                } else {
                                  // For available sessions, use delete
                                  handleDeleteEvent(event.id);
                                }
                              }
                            }}
                            style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
                            title={isReserved || isConfirmed ? "Cancel session" : "Delete event"}
                          ></i>
                        </div>
                      )}
                        <div className="event-content">
                        <div className="event-date-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          {/* Tutor profile photo on the left */}
                          {event.tutorEmail && (
                            <div style={{
                              flexShrink: 0,
                              width: '2.5rem',
                              height: '2.5rem',
                              borderRadius: '50%',
                              background: tutorProfile?.profileImage ? 'transparent' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.875rem',
                              fontWeight: 'bold',
                              overflow: 'hidden',
                              border: '2px solid #e2e8f0'
                            }}>
                              {tutorProfile?.profileImage ? (
                                <img 
                                  src={tutorProfile.profileImage} 
                                  alt={event.tutorName || 'Tutor'} 
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : (
                                getTutorInitials(event.tutorName, event.tutorEmail)
                              )}
                            </div>
                          )}
                          <div style={{ flex: 1 }}>
                            <div className="event-date" style={{ fontWeight: 'bold' }}>
                              {`${monthsOfYear[eventDate.getMonth()]} ${eventDate.getDate()}, ${eventDate.getFullYear()}`}
                            </div>
                            <div className="event-time" style={{ color: '#555' }}>
                              {event.startTime} - {event.endTime}
                            </div>
                          </div>
                        </div>
                        
                        <div className="event-text" style={{ wordBreak: 'break-word' }}>{event.text}</div>
                        
                        {/* Inline layout for participant info and buttons */}
                        <div className="event-participants-and-buttons">
                          <div className="event-participants" style={{ fontSize: 13, color: '#888', lineHeight: 1.4 }}>
                            Tutor: {formatParticipant(event.tutorName, event.tutorEmail)}
                            <br />
                            Student: {formatParticipant(event.studentName, event.studentEmail)}
                          </div>
                          
                          <div className="event-inline-buttons">
                            {showConfirmButton ? (
                              <button
                                className="session-button confirm"
                                disabled={isLoading}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!isLoading) {
                                    await handleConfirmSession(event.id);
                                    // Monthly events will be refreshed automatically in handleConfirmSession
                                  }
                                }}
                              >
                                {isLoading ? 'Confirming...' : 'Confirm'}
                              </button>
                            ) : (
                              <button
                                className={`session-button ${isConfirmed ? 'confirmed' : isReserved ? 'reserved' : 'available'}`}
                                disabled={isReserved || isConfirmed || isLoading}
                                onClick={() => {
                                  if (!isReserved && !isConfirmed && canReserve && !isLoading) {
                                    openPartialReservationModal(event);
                                  }
                                }}
                              >
                                {isLoading && canReserve
                                  ? 'Reserving...'
                                  : isConfirmed
                                    ? 'Confirmed'
                                    : isTutor
                                      ? (isReserved ? 'Reserved' : 'Available')
                                      : (isReserved ? 'Reserved' : 'Reserve')}
                              </button>
                            )}
                            
                            {/* Add Cancel button for reserved and confirmed sessions */}
                            {(isReserved || isConfirmed) && (
                              // Check if user is authorized to cancel (tutor or student in this session)
                              (event.tutorEmail === currentUser?.email || event.studentEmail === currentUser?.email) && (
                                <>
                                  {/* Add Join Google Meet button for confirmed sessions */}
                                  {isConfirmed && event.meetLink && (
                                    <button
                                      className="session-button join-meet"
                                      disabled={isLoading}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(event.meetLink, '_blank', 'noopener,noreferrer');
                                      }}
                                      title="Join Google Meet session"
                                    >
                                      Join Meet
                                    </button>
                                  )}
                                  <button
                                    className="session-button cancel"
                                    disabled={isLoading}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (!isLoading) {
                                        await handleCancelSession(event.id, event);
                                        // Monthly events will be refreshed automatically in handleCancelSession
                                      }
                                    }}
                                  >
                                    {isLoading ? 'Cancelling...' : 'Cancel'}
                                  </button>
                                </>
                              )
                            )}
                          </div>                        </div>
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            </div>
          </div>
        </div>
        );
    }
  };

  const renderSidebar = () => {
    const sidebarContent = (
      <ul className="sidebar-menu">
        <li 
          className={`sidebar-menu-item ${activePage === 'information' ? 'active' : ''}`}
          onClick={() => {
            setActivePage('information');
            if (isMobile) setIsMobileSidebarOpen(false);
          }}
        >
          <i className='bx bx-user'></i>
          My Information
        </li>
        <li 
          className={`sidebar-menu-item ${activePage === 'calendar' ? 'active' : ''}`}
          onClick={() => {
            setActivePage('calendar');
            if (isMobile) setIsMobileSidebarOpen(false);
          }}
        >
          <i className='bx bx-calendar'></i>
          {userType === 'tutor' ? 'Set My Schedule' : 'Calendar'}
        </li>
        <li
          className={`sidebar-menu-item ${activePage === 'mySessions' ? 'active' : ''}`}
          onClick={() => {
            setActivePage('mySessions');
            if (isMobile) setIsMobileSidebarOpen(false);
          }}
        >
          <i className='bx bx-time'></i>
          My Sessions
        </li>
        {userType !== 'tutor' && (
          <li
            className={`sidebar-menu-item ${activePage === 'paymentMethods' ? 'active' : ''}`}
            onClick={() => {
              setActivePage('paymentMethods');
              if (isMobile) setIsMobileSidebarOpen(false);
            }}
          >
            <i className='bx bx-credit-card'></i>
            Payment Methods
          </li>
        )}
      </ul>
    );

    if (isMobile) {
      return (
        <>
          {/* Mobile menu toggle button - positioned in middle left */}
          <button
            className="mobile-menu-toggle"
            onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            style={{
              position: 'fixed',
              top: '50%',
              left: isMobileSidebarOpen ? '280px' : '0px',
              transform: 'translateY(-50%)',
              zIndex: 1002,
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '0 8px 8px 0',
              borderLeft: 'none',
              padding: '15px 8px',
              cursor: 'pointer',
              boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
              fontSize: '16px',
              color: '#374151',
              transition: 'left 0.3s ease',
              width: '40px',
              height: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isMobileSidebarOpen ? (
              <div style={{
                width: '0',
                height: '0',
                borderTop: '6px solid transparent',
                borderBottom: '6px solid transparent',
                borderRight: '8px solid #374151'
              }}></div>
            ) : (
              <div style={{
                width: '0',
                height: '0',
                borderTop: '6px solid transparent',
                borderBottom: '6px solid transparent',
                borderLeft: '8px solid #374151'
              }}></div>
            )}
          </button>

          {/* Mobile sidebar overlay */}
          {isMobileSidebarOpen && (
            <div
              className="mobile-sidebar-overlay"
              onClick={() => setIsMobileSidebarOpen(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1000
              }}
            />
          )}

          {/* Mobile sidebar */}
          <nav
            className={`sidebar mobile-sidebar ${isMobileSidebarOpen ? 'mobile-sidebar-open' : ''}`}
            style={{
              position: 'fixed',
              top: 0,
              left: isMobileSidebarOpen ? 0 : '-280px',
              width: '280px',
              height: '100%',
              backgroundColor: '#ffffff',
              zIndex: 1001,
              transition: 'left 0.3s ease',
              boxShadow: '2px 0 10px rgba(0,0,0,0.1)',
              paddingTop: '20px'
            }}
          >
            {sidebarContent}
          </nav>
        </>
      );
    }

    // Desktop sidebar
    return (
      <nav className="sidebar">
        {sidebarContent}
      </nav>
    );
  };
  return (
    <div style={{ width: '100%', minHeight: '100vh' }}>
      <Helmet>
        <link
          href="https://unpkg.com/boxicons@2.1.4/css/boxicons.min.css"
          rel="stylesheet"
        />
      </Helmet>

      <div className={`dashboard-container ${isLoading ? 'loading-disabled' : ''} ${isMobile ? 'mobile-layout' : ''}`}>
        {renderSidebar()}
        <main className={`main-content ${isMobile ? 'mobile-main-content' : ''}`} style={{
          marginLeft: isMobile ? 0 : undefined,
          paddingTop: isMobile ? '80px' : undefined // Space for mobile menu button
        }}>
          {renderContent()}
        </main>      </div>      {/* Show loading overlay only for calendar and mySessions pages, not for payment/settings pages */}
      <LoadingOverlay 
        isVisible={isLoading && !isNavigatingMonth && (activePage === 'calendar' || activePage === 'mySessions')} 
        message={loadingMessage} 
      />
        {/* Status Message for professional notifications */}
      <StatusMessage 
        message={statusMessage.message}
        type={statusMessage.type}
        isVisible={statusMessage.isVisible}
        onClose={statusMessage.hideMessage}
      />
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        isVisible={confirmationModal.isVisible}
        title={confirmationModal.config.title}
        message={confirmationModal.config.message}
        confirmText={confirmationModal.config.confirmText}
        cancelText={confirmationModal.config.cancelText}
        type={confirmationModal.config.type}
        onConfirm={confirmationModal.handleConfirm}
        onCancel={confirmationModal.handleCancel}
      />
      
      {/* Payment Confirmation Modal */}
      <PaymentConfirmationModal
        isOpen={showPaymentConfirmModal}
        sessionDetails={paymentSessionDetails}
        actionType={pendingPaymentAction?.type}
        onConfirm={async (dontShowAgain) => {
          setShowPaymentConfirmModal(false);
          
          // If user selected "don't show again", update their profile and local state
          if (dontShowAgain) {
            // Update local state immediately so the modal doesn't show again in this session
            setHidePaymentConfirmation(true);
            
            try {
              const response = await api.post('/api/updateUserPreferences', {
                hidePaymentConfirmation: true
              });
              if (!response.ok) {
                const errorData = await response.json();
                console.error('Failed to update preferences:', errorData);
                // Don't revert the local state if it's just a backend save issue
                // setHidePaymentConfirmation(false);
              }
            } catch (error) {
              console.error('Error updating user preferences:', error);
              // If the API call fails, revert the local state
              setHidePaymentConfirmation(false);
            }
          }
          
          // Execute the pending action
          if (pendingPaymentAction) {
            if (pendingPaymentAction.type === 'confirm') {
              await handleConfirmSession(pendingPaymentAction.bookingId, true);
            } else if (pendingPaymentAction.type === 'reserve') {
              await handleReserve(
                pendingPaymentAction.event, 
                pendingPaymentAction.customStartTime,
                pendingPaymentAction.customEndTime,
                true
              );
            }
            setPendingPaymentAction(null);
          }
        }}
        onCancel={() => {
          setShowPaymentConfirmModal(false);
          setPendingPaymentAction(null);
          setPaymentSessionDetails(null);
        }}
      />
    </div>
  );
};

export default BookingDashboard;